import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useFeathers } from '../../app/util';
import { useSelector } from 'react-redux';
import { useParams, Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import { DataDetailToolbar, LoadingSpinner, OfficeCell, DistrictCell, SeatIdentifierCell, ProgressBar, ParsedSampleBallotTile, AddPotentialAutoScouterContent, CandidateMatchCell, AddDistrictTypeModal, PromptEditor, StepTracker } from '../../components';
import DataOperationsTable, { ChangeOfficialMatchPayload } from '../UploadCandidateList/DataOperationsTable';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { District } from '../../app/feathers/districts/District';
import { AutoScouter, CreateOfficialMarker, MatchedRace, MatchedMeasure, PotentialContentType, PotentialSampleBallot, SampleBallotRaw, ParseStatus } from '../../app/feathers/auto-scouter/AutoScouter';
import { DataOperation } from '../../app/feathers/candidate-lists/CandidateList';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import { StateElectionConfiguration } from '../../app/feathers/state-election-configurations/StateElectionConfiguration';
import { Office } from '../../app/feathers/offices/Office';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { Election } from '../../app/feathers/elections/Election';
import { parties } from '../../app/feathers/shared/parties';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import moment from 'moment';

import { stateCodeForName } from '../../app/util/clean-states';

const AUTO_SCOUTER_STEPS = [
  { name: 'Election Homepage', routeName: 'homepage' },
  { name: 'Scraped Content', routeName: 'scraped-content' },
  { name: 'Consolidated Content', routeName: 'consolidated-content' },
  { name: 'Scouting Details', routeName: 'scouting-details' },
  { name: 'Data Operations', routeName: 'data-operations' }
] as const;

const LOW_DISTRICT_CONFIDENCE_THRESHOLD = 0.5;

type AutoScouterStepName = typeof AUTO_SCOUTER_STEPS[number]['routeName'];

const isAutoScouterStepName = (stepName?: string): stepName is AutoScouterStepName => (
  AUTO_SCOUTER_STEPS.some(step => step.routeName === stepName)
);

type AutoScouterDataOperation = NonNullable<AutoScouter['dataOperations']>[number];
type RecentCandidateElectionFields = {
  party?: string;
  incumbent?: boolean;
};

type SaveIndicatorStatus = 'idle' | 'saving' | 'saved' | 'error';

const isCreateOfficialMarker = (value: unknown): value is CreateOfficialMarker => (
  typeof value === 'object' &&
  value !== null &&
  '$create' in value &&
  (value as { $create?: unknown }).$create === true
);

const toActionableDataOperation = (operation: AutoScouterDataOperation): DataOperation => {
  return {
    ...operation,
    operationType: operation.operationType === 'noop' ? 'no-op' : operation.operationType,
    data: operation.data ?? {}
  };
};

interface ScrapedContentItem {
  url: string;
  parseStatus: ParseStatus;
  relevant?: boolean;
  rawBallot: SampleBallotRaw;
  sourceType: PotentialContentType;
  bodyText?: string;
  errorMessage?: string | null;
  warningMessage?: string | null;
  promptLastUsed?: string;
}

interface AutoScouterDetailContentProps {
  electionKey: string;
  autoScouterId: string;
  showToolbar?: boolean;
  enableStepNavigation?: boolean;
  activeStepName?: string;
  createEntitiesTaskId?: string;
  onAutoScouterChange?: (autoScouter: AutoScouter) => void;
  onScoutingConfirmed?: (autoScouter: AutoScouter) => void;
}

const AutoScouterDetailContent = ({
  electionKey,
  autoScouterId,
  showToolbar = true,
  enableStepNavigation = false,
  activeStepName,
  createEntitiesTaskId,
  onAutoScouterChange,
  onScoutingConfirmed
}: AutoScouterDetailContentProps) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const history = useHistory();
  const autoScouterBasePath = `/elections/${electionKey}/auto-scouters/${autoScouterId}`;
  const navigateToStep = (stepName: AutoScouterStepName, replace = false) => {
    if (!enableStepNavigation) return;
    const path = `${autoScouterBasePath}/${stepName}`;
    if (replace) {
      history.replace(path);
    } else {
      history.push(path);
    }
  };
  const electionData = useSelector((state: any) => state?.elections?.byKey[electionKey]);
  const [ election, setElection] = useState<Election | null>(null);
  
  const [autoScouter, setAutoScouter] = useState<AutoScouter | null>(null);
  const [district, setDistrict] = useState<District | null>(null);
  const [offices, setOffices] = useState<Record<string, Office>>({});
  const [raceDistricts, setRaceDistricts] = useState<Record<string, District>>({});
  const [loading, setLoading] = useState(true);
  const [expandedRaces, setExpandedRaces] = useState<Set<string>>(new Set());
  const [expandedMeasures, setExpandedMeasures] = useState<Set<string>>(new Set());
  const [expandedCandidateMetadata, setExpandedCandidateMetadata] = useState<Record<string, boolean>>({});
  const [saveIndicatorStatus, setSaveIndicatorStatus] = useState<SaveIndicatorStatus>('idle');
  const [pendingSaveCount, setPendingSaveCount] = useState(0);
  const pendingSaveCountRef = useRef(0);
  const saveBatchFailedRef = useRef(false);
  const saveIndicatorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addContentInputRef = useRef<HTMLInputElement>(null);
  const [editingCandidate, setEditingCandidate] = useState<{ raceIndex: number; candidateIndex: number; value: string } | null>(null);
  const [newCandidateMatches, setNewCandidateMatches] = useState<Record<number, { official?: string | { $create: true }; createCandidate?: { name: string; party?: string } }>>({});
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [consolidatedContentMoreMenuAnchorEl, setConsolidatedContentMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [confirmingUrl, setConfirmingUrl] = useState<boolean>(false);
  const [confirmingContent, setConfirmingContent] = useState<boolean>(false);
  const [confirmingMatching, setConfirmingMatching] = useState<boolean>(false);
  const [cancellingMatching, setCancellingMatching] = useState<boolean>(false);
  const [confirmingScouting, setConfirmingScouting] = useState<boolean>(false);
  const [regeneratingDataOperations, setRegeneratingDataOperations] = useState<boolean>(false);
  const [rematchingOfficials, setRematchingOfficials] = useState<boolean>(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);
  const [startingCreateEntities, setStartingCreateEntities] = useState<boolean>(false);
  const [addContentModalOpen, setAddContentModalOpen] = useState<boolean>(false);
  const [addContentItemDialog, setAddContentItemDialog] = useState<{
    contentType: 'race' | 'measure';
    error: string;
  } | null>(null);
  const [addingContentItem, setAddingContentItem] = useState<boolean>(false);
  const [removingContentUrls, setRemovingContentUrls] = useState<Set<string>>(new Set());
  const [possibleDistrictsFull, setPossibleDistrictsFull] = useState<District[]>([]);
  const [markedDistrictsSet, setMarkedDistrictsSet] = useState<Set<string>>(new Set());
  const [stateConfiguration, setStateConfiguration] = useState<StateConfiguration | null>(null);
  const [stateElectionConfiguration, setStateElectionConfiguration] = useState<StateElectionConfiguration | null>(null);
  const [savingScoutingConfiguration, setSavingScoutingConfiguration] = useState<boolean>(false);
  const [editingUrl, setEditingUrl] = useState<boolean>(false);
  const [editedUrl, setEditedUrl] = useState<string>('');
  const [savingUrl, setSavingUrl] = useState<boolean>(false);
  const [cancellingCrawl, setCancellingCrawl] = useState<boolean>(false);
  const [cancellingCreateEntities, setCancellingCreateEntities] = useState<boolean>(false);
  const [recrawling, setRecrawling] = useState<boolean>(false);
  const [recrawlDialogOpen, setRecrawlDialogOpen] = useState<boolean>(false);
  const [recrawlPrompt, setRecrawlPrompt] = useState<string>('');
  const [recrawlUrl, setRecrawlUrl] = useState<string>('');
  const [bulkEditMode, setBulkEditMode] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState<boolean>(false);
  const [selectedRaceIndices, setSelectedRaceIndices] = useState<Set<number>>(new Set());
  const [selectedMeasureIndices, setSelectedMeasureIndices] = useState<Set<number>>(new Set());
  const [bulkUpdatingRaces, setBulkUpdatingRaces] = useState<boolean>(false);
  const [bulkUpdatingMeasures, setBulkUpdatingMeasures] = useState<boolean>(false);
  const [splittingContentItemIds, setSplittingContentItemIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<Record<string, boolean>>({}); // Key format: `${raceIndex}-${fieldType}` (e.g., "5-office")
  const [consolidatedContentFilter, setConsolidatedContentFilter] = useState<'all' | 'needs-review'>('all');
  const [duplicateRaceFilter, setDuplicateRaceFilter] = useState<{ key: string; inputText: string } | null>(null);

  const beginTrackedSave = () => {
    if (saveIndicatorTimeoutRef.current) {
      clearTimeout(saveIndicatorTimeoutRef.current);
      saveIndicatorTimeoutRef.current = null;
    }
    if (pendingSaveCountRef.current === 0) {
      saveBatchFailedRef.current = false;
    }
    pendingSaveCountRef.current += 1;
    setPendingSaveCount(pendingSaveCountRef.current);
    setSaveIndicatorStatus('saving');
  };

  const finishTrackedSave = (successful: boolean) => {
    if (!successful) {
      saveBatchFailedRef.current = true;
    }
    pendingSaveCountRef.current = Math.max(0, pendingSaveCountRef.current - 1);
    setPendingSaveCount(pendingSaveCountRef.current);
    if (pendingSaveCountRef.current > 0) return;

    setSaveIndicatorStatus(saveBatchFailedRef.current ? 'error' : 'saved');
    saveIndicatorTimeoutRef.current = setTimeout(() => {
      setSaveIndicatorStatus('idle');
      saveIndicatorTimeoutRef.current = null;
    }, 2000);
  };

  useEffect(() => () => {
    if (saveIndicatorTimeoutRef.current) {
      clearTimeout(saveIndicatorTimeoutRef.current);
    }
  }, []);

  const buildDefaultRecrawlPrompt = () => {
    return [
      'You are starting on the page {startingUrl}, which is the homepage or main elections page for {districtName}, {stateName}. We are trying to find a list of ballot content for the upcoming election: {electionName}.',
      'Your goal is to find any sample ballots and qualified candidate lists that apply to the entire area (not just a single precinct unless that is the only option).',
      'If you find anything related to the {electionName} election, you may need to click on that heading to expand sections or load additional content.',
      'Sometimes, for an entire county, there may be multiple sample ballots (for each municipality or precinct). In that case you should surface a list of those links rather than picking only one.',
      'This ballot content is usually located on a page or under a heading called "Sample ballots", "Sample ballot", "Qualified candidates", or something very similar. Prefer these pages over general election calendars or result pages.',
      'We strongly recommend that you completely scroll the current page first to look for sample ballot or qualified candidate content before clicking into subpages or navigation links that do not clearly mention ballots or candidates.',
      'If you find a list of sample ballots, scroll to the bottom of the list to ensure you are not missing any entries before concluding you are done.',
      'Avoid clicking into pages that are clearly about election results, generic voter lookup tools (like "My Voter Page"), board meeting pages, or general election calendars unless they obviously contain sample ballot or candidate-list content.',
      'As you find links that appear to be sample ballots or candidate lists, you MUST call the function "report_possible_links" with the link labels. You can call this multiple times as you continue exploring; it helps track progress.',
      'When you are confident you have found all relevant links worth exploring, call the function "finish_crawling" exactly once with your final list of links and any useful notes.',
      'If you get stuck, or you opened a tab or PDF that is a dead end, use the function "go_back" to navigate back or close the current tab so you can continue exploring other paths.',
    ].join('\n');
  };

  const getCandidateMetadataFields = (candidate?: {
    withdrawn?: boolean;
    info?: {
      email?: string | null;
      phone?: string | null;
      website?: string | null;
      address?: string | null;
    };
  }) => {
    const info = candidate?.info;
    const fields = [
      { label: 'Email', value: info?.email },
      { label: 'Phone', value: info?.phone },
      { label: 'Website', value: info?.website },
      { label: 'Address', value: info?.address }
    ];
    const metadataFields = fields
      .filter((field): field is { label: string; value: string } => {
        if (typeof field.value !== 'string') return false;
        const normalizedValue = field.value.trim();
        return normalizedValue !== '' && normalizedValue !== 'string';
      })
      .map(field => ({ ...field, value: field.value.trim() }));

    if (candidate?.withdrawn === true) {
      metadataFields.push({ label: 'Withdrawn', value: 'Yes' });
    }

    return metadataFields;
  };

  useEffect(() => {
    if(!feathers) return;
    let active = true;

    const fetchElectionConfiguration = async () => {
      try {
        const resolvedElection = electionData
          ? electionData as Election
          : await feathers.getService('elections').get(electionKey) as Election;
        let resolvedStateElectionConfiguration: StateElectionConfiguration | null = null;
        if (resolvedElection.stateCode) {
          try {
            resolvedStateElectionConfiguration = await feathers
              .getService('state-election-configurations')
              .get(resolvedElection.stateCode.toUpperCase());
          } catch (error) {
            console.error('Error fetching state election configuration:', error);
          }
        }

        if (active) {
          setStateElectionConfiguration(resolvedStateElectionConfiguration);
          setElection(resolvedElection);
        }
      } catch (error) {
        console.error('Error fetching election:', error);
        if (active) {
          setStateElectionConfiguration(null);
        }
      }
    };

    fetchElectionConfiguration();
    return () => {
      active = false;
    };
  }, [feathers, electionKey, electionData]);

  // Calculate which rows need attention and progress
  const races = autoScouter?.consolidatedContent?.races || [];
  const measures = autoScouter?.consolidatedContent?.measures || [];
  const isPartisanElection = election?.partisan === true;
  const showRacePartyColumn = isPartisanElection || races.some(race => race.raceParty != null);
  const partyOptions = useMemo(() => {
    const configuredParties = stateElectionConfiguration?.supportedPartiesForGeneral || [];
    const seenPartyKeys = new Set<string>();

    return configuredParties.flatMap((configuredParty) => {
      const key = (typeof configuredParty === 'string' ? configuredParty : configuredParty.key)?.toUpperCase();
      if (!key || seenPartyKeys.has(key)) return [];

      seenPartyKeys.add(key);
      const configuredName = typeof configuredParty === 'string' ? undefined : configuredParty.name;
      const label = parties[key as keyof typeof parties] || configuredName || key;
      return [{ label, value: key }];
    });
  }, [stateElectionConfiguration?.supportedPartiesForGeneral]);
  
  // Derive partisan race parties from candidates; otherwise use the explicit race party.
  const getRaceParty = (race: MatchedRace): string | null => {
    if (!isPartisanElection) {
      return null;
    }

    if (!race.candidates || race.candidates.length === 0) return null;
    const parties = race.candidates.map(c => c.party).filter(Boolean);
    if (parties.length === 0) return null;
    // Check if all candidates have the same party
    const firstParty = parties[0];
    if (parties.every(p => p === firstParty)) {
      return firstParty || null;
    }
    return null; // Different parties, return null
  };

  // Helper function to create a duplicate key for a race
  const getDuplicateKey = (race: MatchedRace): string | null => {
    const officeKey = race.office?.officeKey;
    const districtId = race.district?.districtId;
    // Only create key if both officeKey and districtId are present
    if (!officeKey || !districtId) return null;
    
    const party = getRaceParty(race);
    const special = race.position?.special || false;
    const seatIdentifier = race.position?.seatIdentifier || '';
    
    return `${districtId}|${officeKey}|${party || 'null'}|${special}|${seatIdentifier || 'null'}`;
  };

  // Calculate duplicate groups by their matched race identity.
  const duplicateRaceIndicesByKey = useMemo(() => {
    const keyToRaces = new Map<string, number[]>();
    
    races.forEach((race, index) => {
      if (race.removed) return;
      const key = getDuplicateKey(race);
      if (key) {
        if (!keyToRaces.has(key)) {
          keyToRaces.set(key, []);
        }
        keyToRaces.get(key)!.push(index);
      }
    });
    
    return new Map(
      [...keyToRaces.entries()].filter(([, indices]) => indices.length > 1)
    );
  }, [races, isPartisanElection]);

  const duplicateGroups = useMemo(() => {
    const duplicates = new Set<number>();
    duplicateRaceIndicesByKey.forEach(indices => {
      indices.forEach(index => duplicates.add(index));
    });
    return duplicates;
  }, [duplicateRaceIndicesByKey]);

  // Determine if a race needs attention
  const needsAttention = (race: MatchedRace, raceIndex: number) => {
    if (race.removed) return false;
    const isUnlabeled = !race.office?.officeKey || !race.district?.districtId;
    const hasWarnings = race.warnings?.includes('different-candidates');
    const isDuplicate = duplicateGroups.has(raceIndex);
    const missingPlurality = race.position?.plurality === undefined || race.position?.plurality === null;
    // Check if election is partisan and race is missing raceParty, or non-partisan and race has raceParty
    const isPartisanElection = election?.partisan === true;
    const missingRaceParty = isPartisanElection && (!race.raceParty || race.raceParty.trim() === '');
    const hasRacePartyWhenNonPartisan = !isPartisanElection && race.raceParty && race.raceParty.trim() !== '';
    // Check if election is non-partisan and any candidate is missing a party
    const hasCandidateMissingParty = !isPartisanElection && race.candidates && race.candidates.some(
      candidate => !candidate.party || candidate.party.trim() === ''
    );
    return isUnlabeled || hasWarnings || isDuplicate || missingPlurality || missingRaceParty || hasRacePartyWhenNonPartisan || hasCandidateMissingParty;
  };

  const measureNeedsAttention = (measure: MatchedMeasure) => {
    if (measure.removed) return false;
    const hasDistrict = Boolean(
      measure?.matchedData?.districtId ||
      measure?.matchedData?.district?._id ||
      measure?.matchedData?.district?.districtId
    );
    const districtConfidence = typeof measure?.matchedData?.confidence === 'number'
      ? measure.matchedData.confidence
      : measure?.matchedData?.district?.confidence;
    const hasLowDistrictConfidence = typeof districtConfidence === 'number' &&
      districtConfidence < LOW_DISTRICT_CONFIDENCE_THRESHOLD;

    return !hasDistrict || hasLowDistrictConfidence;
  };

  const filteredRaceRows = races
    .map((race, index) => ({ race, index }))
    .filter(({ race, index }) => (
      (
        !duplicateRaceFilter
        || getDuplicateKey(race) === duplicateRaceFilter.key
      )
      && (
        consolidatedContentFilter === 'all'
        || (!race.removed && needsAttention(race, index))
      )
    ));
  const selectableFilteredRaces = filteredRaceRows.filter(({ race }) => !race.removed);
  const allFilteredRacesSelected = selectableFilteredRaces.length > 0 &&
    selectableFilteredRaces.every(({ index }) => selectedRaceIndices.has(index));
  const someFilteredRacesSelected = selectableFilteredRaces.some(({ index }) => selectedRaceIndices.has(index));
  const selectedRacesHaveRaceParty = races.some(
    (race, index) => selectedRaceIndices.has(index) && Boolean(race.raceParty)
  );
  const filteredMeasureRows = measures
    .map((measure, index) => ({ measure, index }))
    .filter(({ measure }) => {
      if (duplicateRaceFilter) return false;
      if (consolidatedContentFilter === 'all') return true;
      return !measure.removed && measureNeedsAttention(measure);
    });
  const selectableFilteredMeasures = filteredMeasureRows.filter(({ measure }) => !measure.removed);
  const allFilteredMeasuresSelected = selectableFilteredMeasures.length > 0 &&
    selectableFilteredMeasures.every(({ index }) => selectedMeasureIndices.has(index));
  const someFilteredMeasuresSelected = selectableFilteredMeasures.some(
    ({ index }) => selectedMeasureIndices.has(index)
  );

  const reviewProgress = useMemo(() => {
    if (races.length === 0 && measures.length === 0) {
      return { completed: 0, total: 0, percentage: 0 };
    }

    const completedRaces = races.filter(
      (race, index) => race.removed || !needsAttention(race, index)
    ).length;
    const completedMeasures = measures.filter(
      measure => measure.removed || !measureNeedsAttention(measure)
    ).length;
    const completed = completedRaces + completedMeasures;
    const total = races.length + measures.length;
    const percentage = total > 0 ? (completed / total) * 100 : 0;

    return { completed, total, percentage };
  }, [races, measures, duplicateGroups, election]);

  const isRematchingOfficials =
    rematchingOfficials || autoScouter?.processInProgress?.stage === 'rematching-officials';
  const hasFinishedDataOperations = (autoScouter?.dataOperations || [])
    .some(operation => ['failed', 'completed'].includes(operation.status));
  const shouldDefaultToOperationsTab =
    autoScouter?.processInProgress?.stage === 'creating' || hasFinishedDataOperations;

  // Disable all editing operations if createStatus has a meaningful status (not 'not-started')
  const isEditingDisabled = isRematchingOfficials ||
    (autoScouter?.processInProgress?.stage && autoScouter?.processInProgress?.stage !== 'none') ||
    (autoScouter?.dataOperations || []).some(op => ['failed', 'in-progress', 'completed', 'fixed'].includes(op.status)) ||
    (autoScouter?.createStatus && autoScouter.createStatus === 'success');
  const canChangeOfficialMatches = !isEditingDisabled &&
    (autoScouter?.dataOperations || [])
      .every(operation => ['not-started', 'no-op'].includes(operation.status));

  //autoScouter?.createStatus && autoScouter.createStatus !== 'not-started';

  // Helper function to get warning message text
  const getWarningMessage = (warnings: string[] | undefined): string => {
    if (!warnings || warnings.length === 0) return '';
    
    const warningMessages: Record<string, string> = {
      'different-candidates': 'Ballots contain different candidates',
    };
    
    return warnings.flatMap(w => warningMessages[w] ? [warningMessages[w]] : []).join('; ');
  };

  useEffect(() => {
    if (onAutoScouterChange && autoScouter) {
      onAutoScouterChange(autoScouter);
    }
  }, [autoScouter, onAutoScouterChange]);

  // Handler to suppress the 'different-candidates' warning
  const handleSuppressDifferentCandidatesWarning = async (raceIndex: number) => {
    if (!autoScouter || !autoScouter.consolidatedContent?.races) return;

    const race = autoScouter.consolidatedContent.races[raceIndex];
    if (!race?._id || !race.inputText) return;
    const raceId = race._id;
    const previousWarnings = race.warnings;

    beginTrackedSave();
    setAutoScouter(prev => {
      if (!prev?.consolidatedContent?.races) return prev;
      return {
        ...prev,
        consolidatedContent: {
          ...prev.consolidatedContent,
          races: prev.consolidatedContent.races.map(currentRace => {
            if (currentRace._id !== raceId) return currentRace;
            const warnings = currentRace.warnings?.filter(
              warning => warning !== 'different-candidates'
            );
            return {
              ...currentRace,
              warnings: warnings?.length ? warnings : undefined
            };
          })
        }
      };
    });

    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $updateRaceMatch: {
          heading: race.inputText,
          field: 'differentCandidatesWarning',
          value: false
        }
      });
      finishTrackedSave(true);
    } catch (error) {
      console.error('Error suppressing warning:', error);
      setAutoScouter(prev => {
        if (!prev?.consolidatedContent?.races) return prev;
        return {
          ...prev,
          consolidatedContent: {
            ...prev.consolidatedContent,
            races: prev.consolidatedContent.races.map(currentRace => (
              currentRace._id === raceId
                ? { ...currentRace, warnings: previousWarnings }
                : currentRace
            ))
          }
        };
      });
      finishTrackedSave(false);
    }
  };

  const handleContentItemRemovedChange = async (
    contentType: 'race' | 'measure',
    itemIndex: number,
    removed: boolean
  ) => {
    if (!autoScouter?.consolidatedContent) return;
    const collection = contentType === 'race' ? 'races' : 'measures';
    const item = autoScouter.consolidatedContent[collection]?.[itemIndex];
    if (!item?._id) return;

    const itemId = item._id;
    const previousRemoved = item.removed;
    beginTrackedSave();
    setAutoScouter(prev => {
      if (!prev?.consolidatedContent) return prev;
      return {
        ...prev,
        consolidatedContent: {
          ...prev.consolidatedContent,
          [collection]: (prev.consolidatedContent[collection] || []).map(entry => (
            entry._id === itemId ? { ...entry, removed } : entry
          ))
        }
      };
    });

    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        [removed ? '$removeContentItem' : '$unremoveContentItem']: itemId
      });
      const updatedItem = contentType === 'race'
        ? updatedAutoScouter.consolidatedContent?.races?.find(entry => entry._id === itemId)
        : updatedAutoScouter.consolidatedContent?.measures?.find(entry => entry._id === itemId);
      if (updatedItem) {
        setAutoScouter(prev => {
          if (!prev?.consolidatedContent) return prev;
          return {
            ...prev,
            consolidatedContent: {
              ...prev.consolidatedContent,
              [collection]: (prev.consolidatedContent[collection] || []).map(entry => (
                entry._id === itemId ? updatedItem : entry
              ))
            }
          };
        });
      }
      finishTrackedSave(true);
    } catch (error) {
      console.error(`Error ${removed ? 'removing' : 'restoring'} ${contentType}:`, error);
      setAutoScouter(prev => {
        if (!prev?.consolidatedContent) return prev;
        return {
          ...prev,
          consolidatedContent: {
            ...prev.consolidatedContent,
            [collection]: (prev.consolidatedContent[collection] || []).map(entry => (
              entry._id === itemId ? { ...entry, removed: previousRemoved } : entry
            ))
          }
        };
      });
      finishTrackedSave(false);
    }
  };

  const handleRemoveRace = (raceIndex: number, removed = true) => (
    handleContentItemRemovedChange('race', raceIndex, removed)
  );

  const handleSplitContentItem = async (
    contentType: 'race' | 'measure',
    itemIndex: number
  ) => {
    const collection = contentType === 'race' ? 'races' : 'measures';
    const item = autoScouter?.consolidatedContent?.[collection]?.[itemIndex];
    if (!item?._id || (item.inputTextsUnique?.length || 0) < 2) return;

    const itemId = item._id;
    setSplittingContentItemIds(current => new Set(current).add(itemId));
    beginTrackedSave();
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $splitContentItems: itemId
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      if (contentType === 'race') {
        setSelectedRaceIndices(new Set());
      } else {
        setSelectedMeasureIndices(new Set());
      }
      finishTrackedSave(true);
    } catch (error) {
      console.error(`Error splitting grouped ${contentType} row:`, error);
      finishTrackedSave(false);
    } finally {
      setSplittingContentItemIds(current => {
        const next = new Set(current);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleSplitRace = (raceIndex: number) => (
    handleSplitContentItem('race', raceIndex)
  );

  const handleSplitMeasure = (measureIndex: number) => (
    handleSplitContentItem('measure', measureIndex)
  );

  const handleRemoveMeasure = (measureIndex: number, removed = true) => (
    handleContentItemRemovedChange('measure', measureIndex, removed)
  );

  const handleBulkDeleteRaces = async () => {
    if (!autoScouter?.consolidatedContent?.races || selectedRaceIndices.size === 0) return;

    const selectedRaceIds = autoScouter.consolidatedContent.races
      .filter((race, index) => selectedRaceIndices.has(index) && !race.removed)
      .map(race => race._id)
      .filter((id): id is string => Boolean(id));
    if (selectedRaceIds.length === 0) return;

    setBulkUpdatingRaces(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $removeContentItem: selectedRaceIds
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      setSelectedRaceIndices(new Set());
    } catch (error) {
      console.error('Error removing selected race rows on server:', error);
    } finally {
      setBulkUpdatingRaces(false);
    }
  };

  const handleGroupSelectedRaces = async () => {
    if (!autoScouter?.consolidatedContent?.races || selectedRaceIndices.size < 2) return;

    const selectedRaceIds = autoScouter.consolidatedContent.races
      .filter((race, index) => selectedRaceIndices.has(index) && !race.removed)
      .map(race => race._id)
      .filter((id): id is string => Boolean(id));
    if (selectedRaceIds.length < 2) return;

    setBulkUpdatingRaces(true);
    beginTrackedSave();
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $groupContentItems: selectedRaceIds
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      setSelectedRaceIndices(new Set());
      setDuplicateRaceFilter(null);
      finishTrackedSave(true);
    } catch (error) {
      console.error('Error grouping selected race rows:', error);
      finishTrackedSave(false);
    } finally {
      setBulkUpdatingRaces(false);
    }
  };

  const handleGroupSelectedMeasures = async () => {
    if (!autoScouter?.consolidatedContent?.measures || selectedMeasureIndices.size < 2) return;

    const selectedMeasureIds = autoScouter.consolidatedContent.measures
      .filter((measure, index) => selectedMeasureIndices.has(index) && !measure.removed)
      .map(measure => measure._id)
      .filter((id): id is string => Boolean(id));
    if (selectedMeasureIds.length < 2) return;

    setBulkUpdatingMeasures(true);
    beginTrackedSave();
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $groupContentItems: selectedMeasureIds
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      setSelectedMeasureIndices(new Set());
      finishTrackedSave(true);
    } catch (error) {
      console.error('Error grouping selected measure rows:', error);
      finishTrackedSave(false);
    } finally {
      setBulkUpdatingMeasures(false);
    }
  };

  const handleBulkUnsetRaceParty = async () => {
    if (!autoScouter?.consolidatedContent?.races || selectedRaceIndices.size === 0) return;

    const updates = autoScouter.consolidatedContent.races
      .filter((race, index) => selectedRaceIndices.has(index) && !race.removed && race.inputText)
      .map(race => ({
        heading: race.inputText!,
        field: 'raceParty' as const,
        value: null
      }));
    if (updates.length === 0) return;

    const previousAutoScouter = autoScouter;
    const updatedHeadings = new Set(updates.map(update => update.heading));
    setBulkUpdatingRaces(true);
    beginTrackedSave();
    setAutoScouter(prev => {
      if (!prev?.consolidatedContent?.races) return prev;
      return {
        ...prev,
        consolidatedContent: {
          ...prev.consolidatedContent,
          races: prev.consolidatedContent.races.map(race => (
            race.inputText && updatedHeadings.has(race.inputText)
              ? { ...race, raceParty: null }
              : race
          ))
        }
      };
    });
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $updateRaceMatch: updates
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      finishTrackedSave(true);
      setSelectedRaceIndices(new Set());
    } catch (error) {
      console.error('Error unsetting race party for selected rows:', error);
      setAutoScouter(previousAutoScouter);
      finishTrackedSave(false);
    } finally {
      setBulkUpdatingRaces(false);
    }
  };

  // Unified handler for matchedLabels changes
  const handleMatchedLabelChange = async (
    raceIndex: number,
    changeType: 'office' | 'district' | 'seatIdentifier' | 'special' | 'plurality' | 'raceParty',
    value: Office | District | string | {
      seatIdentifier: string | null;
      seatIdentifierType: string | null;
    } | boolean | number | null
  ) => {
    if (!autoScouter || !autoScouter.consolidatedContent?.races) return;

    const savingKey = `${raceIndex}-${changeType}`;
    setSaving(prev => ({ ...prev, [savingKey]: true }));

    // Update maps for office/district if needed
    if (changeType === 'office' && value !== null && typeof value === 'object' && 'key' in value) {
      const newOffice = value as Office;
      setOffices(prevOffices => {
        if (prevOffices[newOffice.key]) return prevOffices;
        return { ...prevOffices, [newOffice.key]: newOffice };
      });
    } else if (changeType === 'district' && value !== null && typeof value === 'object' && '_id' in value) {
      const newDistrict = value as District;
      setRaceDistricts(prevDistricts => {
        if (prevDistricts[newDistrict._id]) return prevDistricts;
        return { ...prevDistricts, [newDistrict._id]: newDistrict };
      });
    }

    const race = autoScouter.consolidatedContent.races[raceIndex];
    if (!race?._id || !race.inputText) {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
      return;
    }

    const previousAutoScouter = autoScouter;
    const heading = race.inputText;

    beginTrackedSave();
    try {
      let commandValue: string | number | boolean | null | {
        seatIdentifier: string | null;
        seatIdentifierType: string | null;
      };
      if (changeType === 'district') {
        commandValue = value === null ? null : (value as District)._id;
      } else if (changeType === 'office') {
        commandValue = value === null ? null : (value as Office).key;
      } else {
        commandValue = value as string | boolean | number | null | {
          seatIdentifier: string | null;
          seatIdentifierType: string | null;
        };
      }

      setAutoScouter(prev => {
        if (!prev?.consolidatedContent?.races) return prev;
        return {
          ...prev,
          consolidatedContent: {
            ...prev.consolidatedContent,
            races: prev.consolidatedContent.races.map(currentRace => {
              if (currentRace._id !== race._id) return currentRace;

              if (changeType === 'district') {
                return {
                  ...currentRace,
                  district: {
                    ...(currentRace.district || {}),
                    districtId: commandValue as string | null,
                    confidence: commandValue === null ? undefined : 1,
                    createWithData: undefined
                  }
                };
              }
              if (changeType === 'office') {
                return {
                  ...currentRace,
                  office: {
                    ...(currentRace.office || {}),
                    officeKey: commandValue as string | null,
                    confidence: commandValue === null ? undefined : 1,
                    createWithTitle: undefined
                  }
                };
              }
              if (changeType === 'seatIdentifier') {
                const position = commandValue as {
                  seatIdentifier: string | null;
                  seatIdentifierType: string | null;
                };
                return {
                  ...currentRace,
                  position: {
                    ...(currentRace.position || {}),
                    ...position
                  }
                };
              }
              if (changeType === 'special') {
                return {
                  ...currentRace,
                  position: {
                    ...(currentRace.position || {}),
                    special: commandValue as boolean
                  }
                };
              }
              if (changeType === 'plurality') {
                return {
                  ...currentRace,
                  position: {
                    ...(currentRace.position || {}),
                    plurality: commandValue as number | null
                  }
                };
              }
              return {
                ...currentRace,
                raceParty: commandValue as string | null
              };
            })
          }
        };
      });

      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $updateRaceMatch: {
          heading,
          field: changeType,
          value: commandValue
        }
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      finishTrackedSave(true);
    } catch (error) {
      console.error(`Error saving ${changeType} change to server:`, error);
      setAutoScouter(previousAutoScouter);
      finishTrackedSave(false);
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };

  // Handler for office changes
  const handleOfficeChange = async (raceIndex: number, newOffice: Office, confidence: number) => {
    await handleMatchedLabelChange(raceIndex, 'office', newOffice);
  };

  // Handler for district changes
  const handleDistrictChange = async (raceIndex: number, newDistrict: District, confidence: number) => {
    await handleMatchedLabelChange(raceIndex, 'district', newDistrict);
  };

  // Handler for measure district changes in consolidated content
  const handleMeasureDistrictChange = async (measureIndex: number, newDistrict: District | null) => {
    if (!autoScouter || !autoScouter.consolidatedContent?.measures) return;
    const targetMeasure = autoScouter.consolidatedContent.measures[measureIndex];
    const heading = targetMeasure?.inputText;
    if (!targetMeasure?._id || !heading) return;
    const confidence = newDistrict ? 1.0 : null;

    const savingKey = `measure-${measureIndex}-district`;
    setSaving(prev => ({ ...prev, [savingKey]: true }));
    const previousAutoScouter = autoScouter;

    if (newDistrict) {
      setRaceDistricts(prevDistricts => {
        if (prevDistricts[newDistrict._id]) return prevDistricts;
        return { ...prevDistricts, [newDistrict._id]: newDistrict };
      });
    }

    const updatedMeasures = autoScouter.consolidatedContent.measures.map(measure => {
      if (measure._id !== targetMeasure._id) return measure;
      const updatedMatchedData: Record<string, any> = {
        ...(measure.matchedData || {}),
        districtId: newDistrict?._id || null,
        confidence,
        createWithData: null
      };
      delete updatedMatchedData.district;
      return {
        ...measure,
        matchedData: updatedMatchedData
      };
    });

    const updatedConsolidatedContent = {
      ...autoScouter.consolidatedContent,
      measures: updatedMeasures
    };

    setAutoScouter(prev => prev ? ({ ...prev, consolidatedContent: updatedConsolidatedContent }) : prev);

    beginTrackedSave();
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $updateMeasureMatch: {
          heading,
          field: 'district',
          value: newDistrict
            ? {
                districtId: newDistrict._id,
                confidence: 1.0
              }
            : null
        }
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      finishTrackedSave(true);
    } catch (error) {
      console.error('Error updating measure district:', error);
      setAutoScouter(previousAutoScouter);
      finishTrackedSave(false);
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };

  const handleMeasureDistrictConfidenceChange = async (measureIndex: number, confidence: number) => {
    if (!autoScouter || !autoScouter.consolidatedContent?.measures) return;
    const targetMeasure = autoScouter.consolidatedContent.measures[measureIndex];
    const heading = targetMeasure?.inputText;
    if (!targetMeasure?._id || !heading) return;

    const savingKey = `measure-${measureIndex}-district`;
    const previousAutoScouter = autoScouter;
    const updatedMeasures = autoScouter.consolidatedContent.measures.map(measure => {
      if (measure._id !== targetMeasure._id) return measure;
      return {
        ...measure,
        matchedData: {
          ...(measure.matchedData || {}),
          confidence
        }
      };
    });
    const updatedConsolidatedContent = {
      ...autoScouter.consolidatedContent,
      measures: updatedMeasures
    };

    setSaving(prev => ({ ...prev, [savingKey]: true }));
    setAutoScouter(prev => prev ? ({ ...prev, consolidatedContent: updatedConsolidatedContent }) : prev);

    beginTrackedSave();
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $updateMeasureMatch: {
          heading,
          field: 'confidence',
          value: confidence
        }
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      finishTrackedSave(true);
    } catch (error) {
      console.error('Error saving measure district confidence change:', error);
      setAutoScouter(previousAutoScouter);
      finishTrackedSave(false);
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };

  const handleMeasureFieldChange = async (
    measureIndex: number,
    field: 'measureName' | 'ballotText' | 'actNumbers',
    value: string
  ) => {
    if (!autoScouter || !autoScouter.consolidatedContent?.measures) return;
    const targetMeasure = autoScouter.consolidatedContent.measures[measureIndex];
    const heading = targetMeasure?.inputText;
    if (!targetMeasure?._id || !heading) return;

    const measureId = targetMeasure._id;
    const previousValue = targetMeasure[field];

    beginTrackedSave();
    setAutoScouter(previous => {
      if (!previous?.consolidatedContent?.measures) return previous;
      return {
        ...previous,
        consolidatedContent: {
          ...previous.consolidatedContent,
          measures: previous.consolidatedContent.measures.map(measure => (
            measure._id === measureId ? { ...measure, [field]: value } : measure
          ))
        }
      };
    });

    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $updateMeasureMatch: {
          heading,
          field,
          value
        }
      });
      finishTrackedSave(true);
    } catch (error) {
      console.error(`Error updating measure ${field}:`, error);
      setAutoScouter(previous => {
        if (!previous?.consolidatedContent?.measures) return previous;
        return {
          ...previous,
          consolidatedContent: {
            ...previous.consolidatedContent,
            measures: previous.consolidatedContent.measures.map(measure => (
              measure._id === measureId && measure[field] === value
                ? { ...measure, [field]: previousValue }
                : measure
            ))
          }
        };
      });
      setErrorMessage(`Failed to update measure ${field === 'measureName' ? 'title' : field}. Please try again.`);
      setErrorDialogOpen(true);
      finishTrackedSave(false);
    }
  };

  // Handler for seat identifier changes
  const handleSeatIdentifierChange = async (raceIndex: number, seatIdentifier: string | null, seatIdentifierType: string | null) => {
    await handleMatchedLabelChange(raceIndex, 'seatIdentifier', { seatIdentifier, seatIdentifierType });
  };

  // Handler for confidence changes
  const handleRaceConfidenceChange = async (
    raceIndex: number,
    matchField: 'office' | 'district',
    confidence: number
  ) => {
    const race = autoScouter?.consolidatedContent?.races?.[raceIndex];
    if (!race?._id || !race.inputText) return;

    const raceId = race._id;
    const previousConfidence = race[matchField]?.confidence;
    beginTrackedSave();
    setAutoScouter(previous => {
      if (!previous?.consolidatedContent?.races) return previous;
      return {
        ...previous,
        consolidatedContent: {
          ...previous.consolidatedContent,
          races: previous.consolidatedContent.races.map(currentRace => (
            currentRace._id === raceId
              ? {
                  ...currentRace,
                  [matchField]: {
                    ...(currentRace[matchField] || {}),
                    confidence
                  }
                }
              : currentRace
          ))
        }
      };
    });

    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $updateRaceMatch: {
          heading: race.inputText,
          field: matchField === 'office' ? 'officeConfidence' : 'districtConfidence',
          value: confidence
        }
      });
      finishTrackedSave(true);
    } catch (error) {
      console.error(`Error saving ${matchField} confidence change:`, error);
      setAutoScouter(previous => {
        if (!previous?.consolidatedContent?.races) return previous;
        return {
          ...previous,
          consolidatedContent: {
            ...previous.consolidatedContent,
            races: previous.consolidatedContent.races.map(currentRace => (
              currentRace._id === raceId
                ? {
                    ...currentRace,
                    [matchField]: {
                      ...(currentRace[matchField] || {}),
                      confidence: previousConfidence
                    }
                  }
                : currentRace
            ))
          }
        };
      });
      finishTrackedSave(false);
    }
  };

  const handleOfficeConfidenceChange = (
    raceIndex: number,
    confidence: number
  ) => handleRaceConfidenceChange(raceIndex, 'office', confidence);

  const handleDistrictConfidenceChange = (
    raceIndex: number,
    confidence: number
  ) => handleRaceConfidenceChange(raceIndex, 'district', confidence);

  // Handler for special election changes
  const handleSpecialElectionChange = async (raceIndex: number, isSpecial: boolean) => {
    await handleMatchedLabelChange(raceIndex, 'special', isSpecial);
  };

  // Handler for plurality changes
  const handlePluralityChange = async (raceIndex: number, plurality: number | null) => {
    await handleMatchedLabelChange(raceIndex, 'plurality', plurality);
  };

  const handleRacePartyChange = async (raceIndex: number, raceParty: string | null) => {
    await handleMatchedLabelChange(raceIndex, 'raceParty', raceParty);
  };

  // Handler to retrigger matching
  const handleRetriggerMatching = async () => {
    if (!autoScouter || !autoScouterId) return;

    setMoreMenuAnchorEl(null);

    try {
      // Check if we have sampleBallotsRaw to retrigger with
      if (!autoScouter.sampleBallotsRaw || autoScouter.sampleBallotsRaw.length === 0) {
        console.error('Cannot retrigger matching: no sampleBallotsRaw available');
        // Could show a toast/notification here
        return;
      }

      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $matchBallotContent: { reset: false }
      });

      // Refresh the auto-scouter data to get updated status
      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
    } catch (error) {
      console.error('Error retriggering matching:', error);
    }
  };

  // Handler to delete auto-scouter
  const handleDelete = async () => {
    if (!autoScouter || !autoScouterId) return;

    setMoreMenuAnchorEl(null);

    // Confirm deletion
    const confirmed = window.confirm('Are you sure you want to delete this auto-scouter? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    try {
      await feathers.getService('auto-scouter').remove(autoScouterId);
      // Navigate back to the auto-scouters list
      history.push(`/elections/${electionKey}/auto-scouters`);
    } catch (error) {
      console.error('Error deleting auto-scouter:', error);
      alert('Failed to delete auto-scouter. Please try again.');
    }
  };

  // Handler to confirm URL
  const handleConfirmUrl = async () => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    setConfirmingUrl(true);
    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $reviewUpdate: {
          field: 'url',
          confirmed: true
        }
      });
      
      // Refresh the auto-scouter data to get updated review status
      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
      navigateToStep('scraped-content');
    } catch (error) {
      console.error('Error confirming URL:', error);
      alert('Failed to confirm URL. Please try again.');
    } finally {
      setConfirmingUrl(false);
    }
  };

  // Handler to cancel URL confirmation
  const handleCancelUrl = () => {
    setErrorDialogOpen(true);
  };

  // Handler to start editing URL
  const handleStartEditUrl = () => {
    setEditedUrl(autoScouter?.url || '');
    setEditingUrl(true);
  };

  // Handler to cancel editing URL
  const handleCancelEditUrl = () => {
    setEditingUrl(false);
    setEditedUrl('');
  };

  // Handler to save edited URL
  const handleSaveUrl = async () => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    setSavingUrl(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        url: editedUrl,
        $updateUrl: true // Special flag to trigger clearing content and re-crawling
      });
      
      setAutoScouter(updatedAutoScouter);
      setEditingUrl(false);
      setEditedUrl('');
    } catch (error) {
      console.error('Error saving URL:', error);
      alert('Failed to save URL. Please try again.');
    } finally {
      setSavingUrl(false);
    }
  };

  // Handler to cancel crawl and move to next stage
  const handleCancelCrawl = async () => {
    if (!autoScouterId || !feathers) return;

    setCancellingCrawl(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $cancelCrawl: true
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
    } catch (error) {
      console.error('Error cancelling crawl:', error);
      alert('Failed to cancel crawl. Please try again.');
    } finally {
      setCancellingCrawl(false);
    }
  };

  const handleOpenRecrawlDialog = () => {
    const previousPrompt = autoScouter?.crawlMetadata?.crawlInstructions?.trim();
    setRecrawlPrompt(previousPrompt || buildDefaultRecrawlPrompt());
    setRecrawlUrl(autoScouter?.url || '');
    setRecrawlDialogOpen(true);
  };

  const handleRecrawl = async () => {
    if (!autoScouterId || !feathers) return;

    setRecrawling(true);
    try {
      const trimmedPrompt = recrawlPrompt.trim();
      const trimmedUrl = recrawlUrl.trim();
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $recrawl: {
          ...(trimmedPrompt ? { prompt: trimmedPrompt } : {}),
          ...(trimmedUrl ? { url: trimmedUrl } : {})
        }
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      setRecrawlDialogOpen(false);
    } catch (error) {
      console.error('Error re-crawling:', error);
      alert('Failed to re-crawl. Please try again.');
    } finally {
      setRecrawling(false);
    }
  };

  // Handler to add new content
  const handleAddContent = () => {
    setAddContentModalOpen(true);
  };

  // Get existing URLs for duplicate checking
  const existingUrls = useMemo(() => {
    if (!autoScouter) return [];
    return (autoScouter.potentialSampleBallots || []).map(b => b.url);
  }, [autoScouter]);

  // Handler to save new content
  const handleSaveNewContent = async (contentType: PotentialContentType, url: string, dataType?: 'pdf' | 'body-text', bodyText?: string) => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    const newContent: PotentialSampleBallot = {
      contentType,
      url,
      dataType: dataType || 'pdf',
      bodyText: dataType === 'body-text' ? bodyText : undefined,
      parseStatus: 'not-started',
      relevant: undefined
    };

    await feathers.getService('auto-scouter').patch(autoScouterId, {
      $addPotentialContent: newContent
    });

    // Refresh the auto-scouter data
    const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
    setAutoScouter(updatedAutoScouter);
  };

  const handleRemoveContent = async (item: ScrapedContentItem) => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    const confirmed = window.confirm(
      `Remove this ${item.sourceType === 'candidate-list' ? 'candidate list' : item.sourceType === 'measure-list' ? 'measure/proposal list' : 'sample ballot'}?\n\n${item.url}`
    );
    if (!confirmed) return;

    setRemovingContentUrls(previous => new Set(previous).add(item.url));
    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $removePotentialContent: {
          contentType: item.sourceType,
          url: item.url
        }
      });

      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
      setSelectedItems(previous => {
        const next = new Set(previous);
        next.delete(item.url);
        return next;
      });
    } catch (error) {
      console.error('Error removing content:', error);
      alert('Failed to remove content. Please try again.');
    } finally {
      setRemovingContentUrls(previous => {
        const next = new Set(previous);
        next.delete(item.url);
        return next;
      });
    }
  };

  // Handler to mark selected items as not relevant in bulk
  const handleBulkMarkAsNotRelevant = async () => {
    if (!feathers || selectedItems.size === 0) return;
    
    setBulkUpdating(true);
    try {
      // Mark all selected items as not relevant in parallel
      const updatePromises = Array.from(selectedItems).map(url => {
        const item = scrapedContentItems.find(i => i.url === url);
        if (!item) return Promise.resolve();
        
        return feathers.getService('auto-scouter').patch(autoScouterId, {
          $markRelevancy: {
            contentType: item.sourceType,
            url: url,
            relevant: false
          }
        });
      });
      
      await Promise.all(updatePromises);
      
      // Reload the auto-scouter
      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
      
      // Clear selection and exit bulk edit mode
      setSelectedItems(new Set());
      setBulkEditMode(false);
    } catch (error) {
      console.error('Error marking items as not relevant:', error);
      alert('Error marking items as not relevant. Please try again.');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Handler to confirm content
  const handleConfirmContent = async () => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    setConfirmingContent(true);
    try {
      // Also confirm URL if not already confirmed
      if (!autoScouter.review?.url) {
        await feathers.getService('auto-scouter').patch(autoScouterId, {
          $reviewUpdate: {
            field: 'url',
            confirmed: true
          }
        });
      }

      // Confirm content
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $reviewUpdate: {
          field: 'content',
          confirmed: true
        }
      });
      
      // Refresh the auto-scouter data to get updated review status
      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
      navigateToStep('consolidated-content');
    } catch (error) {
      console.error('Error confirming content:', error);
      alert('Failed to confirm content. Please try again.');
    } finally {
      setConfirmingContent(false);
    }
  };

  // Handler to confirm matching (final review step)
  const handleConfirmMatching = async () => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    setConfirmingMatching(true);
    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        dataOperations: [],
        $reviewUpdate: {
          field: 'matching',
          confirmed: true
        }
      });
      
      // Refresh the auto-scouter data to get updated review status and dataOperations
      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
      navigateToStep(isScoutingConfirmed ? 'data-operations' : 'scouting-details');
    } catch (error) {
      console.error('Error confirming matching:', error);
      alert('Failed to confirm matching. Please try again.');
    } finally {
      setConfirmingMatching(false);
    }
  };

  // Handler to confirm scouting
  const handleConfirmScouting = async () => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    setConfirmingScouting(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $reviewUpdate: {
          field: 'scouting',
          confirmed: true
        }
      });
      
      // Refresh the auto-scouter data to get updated review status
      setAutoScouter(updatedAutoScouter);
      navigateToStep('data-operations');
      if (onScoutingConfirmed) {
        onScoutingConfirmed(updatedAutoScouter as AutoScouter);
      }
    } catch (error: any) {
      console.error('Error confirming scouting:', error);
      // Extract error message from Feathers error
      const errorMsg = error?.message || error?.error?.message || 'Failed to confirm scouting. Please try again.';
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
    } finally {
      setConfirmingScouting(false);
    }
  };

  const handleRegenerateDataOperations = async () => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    const confirmed = window.confirm(
      'Regenerate data operations? Any manual changes to the current queue will be replaced.'
    );
    if (!confirmed) return;

    setRegeneratingDataOperations(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $reviewUpdate: {
          field: 'scouting',
          confirmed: true
        }
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
    } catch (error: any) {
      console.error('Error regenerating data operations:', error);
      const errorMsg = error?.message || error?.error?.message || 'Failed to regenerate data operations. Please try again.';
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
    } finally {
      setRegeneratingDataOperations(false);
    }
  };

  // Handler to save markedDistricts
  const handleSaveMarkedDistricts = async (newMarkedDistricts: string[]) => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        markedDistricts: newMarkedDistricts
      });
      
      // Refresh the auto-scouter data
      setAutoScouter(updatedAutoScouter);
      setMarkedDistrictsSet(new Set(newMarkedDistricts));
    } catch (error) {
      console.error('Error saving markedDistricts:', error);
      alert('Failed to save marked districts. Please try again.');
    }
  };

  // Handler to save scoutingConfiguration
  const handleSaveScoutingConfiguration = async (newScoutingConfiguration: AutoScouter['scoutingConfiguration']) => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    setSavingScoutingConfiguration(true);
    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        scoutingConfiguration: newScoutingConfiguration
      });
      
      // Refresh the auto-scouter data
      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
    } catch (error) {
      console.error('Error saving scoutingConfiguration:', error);
      alert('Failed to save scouting configuration. Please try again.');
    } finally {
      setSavingScoutingConfiguration(false);
    }
  };

  const handleResetScoutingConfiguration = async () => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    setSavingScoutingConfiguration(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $resetScoutingConfiguration: true
      });
      setAutoScouter(updatedAutoScouter);
    } catch (error: any) {
      console.error('Error resetting scoutingConfiguration:', error);
      const errorMsg = error?.message || error?.error?.message || 'Failed to reset scouting configuration. Please try again.';
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
    } finally {
      setSavingScoutingConfiguration(false);
    }
  };

  // Handler to save completion content-type toggles
  const handleSaveCompletionTypes = async (updates: { completeRaces?: boolean; completeMeasures?: boolean }) => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    setSavingScoutingConfiguration(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, updates);
      setAutoScouter(updatedAutoScouter);
    } catch (error) {
      console.error('Error saving completion content-type toggles:', error);
      alert('Failed to save completion content-type toggles. Please try again.');
    } finally {
      setSavingScoutingConfiguration(false);
    }
  };

  // Handler to save data operations
  const handleSaveDataOperations = async (operations: AutoScouter['dataOperations']) => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    const operationsRevert = autoScouter.dataOperations?.slice() || [];
    
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        dataOperations: operations
      });

      setAutoScouter(updatedAutoScouter);
    } catch (error) {
      console.error('Error saving data operations:', error);
      // Revert on error
      setAutoScouter(prev => prev ? { ...prev, dataOperations: operationsRevert } : prev);
    }
  };

  const handleChangeOfficialMatch = async (change: ChangeOfficialMatchPayload) => {
    if (!autoScouter || !autoScouterId || !feathers) {
      throw new Error('Cannot change an official match before the auto-scouter has loaded.');
    }

    const matchingRace = (autoScouter.consolidatedContent?.races || []).find(race => {
      const uniqueId = `${race.office?.officeKey}-${race.district?.districtId}-${election?.partisan === true ? race.raceParty ?? 'no-party' : 'no-party'}-${race.position?.seatIdentifier ?? 'no-seat-identifier'}-${race.position?.special ? 'special' : 'not-special'}`;
      return uniqueId === change.raceUniqueId;
    });
    if (!matchingRace?._id) {
      throw new Error('The candidate race could not be located. Refresh and try again.');
    }
    const exactCandidate = matchingRace.candidates?.find(candidate =>
      candidate.name === change.candidateName
    );
    const candidatesWithPreviousMatch = (matchingRace.candidates || []).filter(candidate => (
      typeof change.previousOfficial === 'string'
        ? candidate.official === change.previousOfficial
        : isCreateOfficialMarker(candidate.official)
    ));
    const sourceCandidate = exactCandidate ||
      (candidatesWithPreviousMatch.length === 1 ? candidatesWithPreviousMatch[0] : undefined);
    if (!sourceCandidate?.name) {
      throw new Error('The candidate could not be located. Refresh and try again.');
    }

    const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
      $updateCandidateMatch: {
        consolidatedContentId: matchingRace._id,
        candidateName: sourceCandidate.name,
        field: 'official',
        value: change.nextOfficial
      }
    });
    setAutoScouter(updatedAutoScouter);
  };

  const destructiveDataOperations = (autoScouter?.dataOperations || []).filter(
    operation => (
      operation.operationType === 'delete'
      && (operation.entityType === 'races' || operation.entityType === 'measures')
    )
  );
  const raceDeleteCount = destructiveDataOperations.filter(
    operation => operation.entityType === 'races'
  ).length;
  const measureDeleteCount = destructiveDataOperations.filter(
    operation => operation.entityType === 'measures'
  ).length;

  const createEntities = async () => {
    if (!autoScouter || !autoScouterId || !feathers) return;

    setStartingCreateEntities(true);
    try {
      const response = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $createEntities: true,
        ...(createEntitiesTaskId ? { taskId: createEntitiesTaskId } : {})
      });
      
      // Update the local auto-scouter state
      setAutoScouter(response);
      
      console.log('Entity creation started:', response);
    } catch (error) {
      console.error('Error starting entity creation:', error);
      alert('Failed to start entity creation. Please try again.');
    } finally {
      setStartingCreateEntities(false);
    }
  };

  // Handler to create entities from data operations
  const handleCreateEntities = () => {
    if (destructiveDataOperations.length > 0) {
      setDeleteConfirmationOpen(true);
      return;
    }

    createEntities();
  };

  const handleCancelCreateEntities = async () => {
    if (
      !autoScouterId ||
      !feathers ||
      autoScouter?.processInProgress?.stage !== 'creating'
    ) return;

    setCancellingCreateEntities(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $cancelCreateEntities: true
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
    } catch (error: any) {
      console.error('Error cancelling entity creation:', error);
      setErrorMessage(
        error?.message ||
        error?.error?.message ||
        'Failed to cancel entity creation. Please try again.'
      );
      setErrorDialogOpen(true);
    } finally {
      setCancellingCreateEntities(false);
    }
  };

  // Handler to toggle race expansion
  const toggleRaceExpansion = (raceId?: string) => {
    if (!raceId) return;
    setExpandedRaces(prev => {
      const next = new Set(prev);
      if (next.has(raceId)) {
        next.delete(raceId);
      } else {
        next.add(raceId);
      }
      return next;
    });
  };

  const toggleMeasureExpansion = (measureId?: string) => {
    if (!measureId) return;
    setExpandedMeasures(prev => {
      const next = new Set(prev);
      if (next.has(measureId)) {
        next.delete(measureId);
      } else {
        next.add(measureId);
      }
      return next;
    });
  };

  const updateCandidateFields = async (
    raceIndex: number,
    candidateIndex: number,
    updates: {
      official?: string | CreateOfficialMarker;
      party?: string;
      name?: string;
    }
  ): Promise<boolean> => {
    const race = autoScouter?.consolidatedContent?.races?.[raceIndex];
    const candidate = race?.candidates?.[candidateIndex];
    if (!race?._id || !candidate?.name) return false;

    const raceId = race._id;
    const candidateName = candidate.name;
    const optimisticName = updates.name?.trim() || candidateName;
    const previousValues = {
      official: candidate.official,
      party: candidate.party,
      name: candidate.name
    };

    beginTrackedSave();
    setAutoScouter(previous => {
      if (!previous?.consolidatedContent?.races) return previous;
      return {
        ...previous,
        consolidatedContent: {
          ...previous.consolidatedContent,
          races: previous.consolidatedContent.races.map(currentRace => (
            currentRace._id === raceId
              ? {
                  ...currentRace,
                  candidates: (currentRace.candidates || []).map(currentCandidate => (
                    currentCandidate.name === candidateName
                      ? { ...currentCandidate, ...updates, name: optimisticName }
                      : currentCandidate
                  ))
                }
              : currentRace
          ))
        }
      };
    });

    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $updateCandidateMatch: {
          consolidatedContentId: raceId,
          candidateName,
          updates
        }
      });
      finishTrackedSave(true);
      return true;
    } catch (error) {
      console.error('Error updating candidate match:', error);
      setAutoScouter(previous => {
        if (!previous?.consolidatedContent?.races) return previous;
        return {
          ...previous,
          consolidatedContent: {
            ...previous.consolidatedContent,
            races: previous.consolidatedContent.races.map(currentRace => (
              currentRace._id === raceId
                ? {
                    ...currentRace,
                    candidates: (currentRace.candidates || []).map(currentCandidate => (
                      currentCandidate.name === optimisticName
                        ? {
                            ...currentCandidate,
                            ...(updates.official !== undefined
                              ? { official: previousValues.official }
                              : {}),
                            ...(updates.party !== undefined
                              ? { party: previousValues.party }
                              : {}),
                            ...(updates.name !== undefined
                              ? { name: previousValues.name }
                              : {})
                          }
                        : currentCandidate
                    ))
                  }
                : currentRace
            ))
          }
        };
      });
      finishTrackedSave(false);
      return false;
    }
  };

  // Handler to update candidate official/createCandidate
  const handleCandidateMatchChange = async (raceIndex: number, candidateIndex: number, value: { official?: string | { $create: true }; createCandidate?: { name: string; party?: string } }) => {
    const candidate = autoScouter?.consolidatedContent?.races?.[raceIndex]?.candidates?.[candidateIndex];
    if (!candidate?.name) return;

    const updates: {
      official?: string | CreateOfficialMarker;
      name?: string;
    } = {};
    if (value.official !== undefined) {
      const officialChanged = typeof value.official === 'string'
        ? value.official !== candidate.official
        : !isCreateOfficialMarker(candidate.official);
      if (officialChanged) {
        updates.official = value.official;
      }
    }

    const updatedName = value.createCandidate?.name?.trim();
    if (updatedName && updatedName !== candidate.name) {
      updates.name = updatedName;
    }
    if (Object.keys(updates).length > 0) {
      await updateCandidateFields(raceIndex, candidateIndex, updates);
    }
  };

  // Allow party edits even when candidate is matched to an official
  const handleCandidatePartyChange = async (raceIndex: number, candidateIndex: number, party: string) => {
    await updateCandidateFields(raceIndex, candidateIndex, { party });
  };

  // Handler to remove candidate
  const handleRemoveCandidate = async (raceIndex: number, candidateIndex: number) => {
    if (!autoScouter || !autoScouter.consolidatedContent?.races) return;

    const race = autoScouter.consolidatedContent.races[raceIndex];
    if (!race || !race.candidates || !Array.isArray(race.candidates)) return;

    // Get the candidate at the specified index
    const candidate = race.candidates[candidateIndex];
    if (!candidate) return;

    // Use candidate name - this should be unique within a race after consolidation
    const candidateName = candidate.name;
    if (!candidateName || !race._id) return;
    const raceId = race._id;

    beginTrackedSave();
    setAutoScouter(previous => {
      if (!previous?.consolidatedContent?.races) return previous;
      return {
        ...previous,
        consolidatedContent: {
          ...previous.consolidatedContent,
          races: previous.consolidatedContent.races.map(currentRace => (
            currentRace._id === raceId
              ? {
                  ...currentRace,
                  candidates: (currentRace.candidates || []).filter(
                    currentCandidate => !(
                      (
                        typeof candidate.official === 'string'
                        && currentCandidate.official === candidate.official
                      )
                      || currentCandidate.name === candidateName
                    )
                  )
                }
              : currentRace
          ))
        }
      };
    });

    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $removeCandidateFromGroup: {
          candidateName,
          rowId: raceId
        }
      });
      finishTrackedSave(true);
    } catch (error) {
      console.error('Error removing candidate:', error);
      setAutoScouter(previous => {
        if (!previous?.consolidatedContent?.races) return previous;
        return {
          ...previous,
          consolidatedContent: {
            ...previous.consolidatedContent,
            races: previous.consolidatedContent.races.map(currentRace => {
              if (currentRace._id !== raceId) return currentRace;
              const candidates = [...(currentRace.candidates || [])];
              const candidateAlreadyPresent = candidates.some(currentCandidate => (
                (
                  typeof candidate.official === 'string'
                  && currentCandidate.official === candidate.official
                )
                || currentCandidate.name === candidateName
              ));
              if (!candidateAlreadyPresent) {
                candidates.splice(Math.min(candidateIndex, candidates.length), 0, candidate);
              }
              return { ...currentRace, candidates };
            })
          }
        };
      });
      finishTrackedSave(false);
    }
  };

  // Handler to add new candidate
  const handleAddCandidate = async (raceIndex: number, matchValue: { official?: string | { $create: true }; createCandidate?: { name: string; party?: string } }) => {
    if (!autoScouter || !autoScouter.consolidatedContent?.races) return;

    const race = autoScouter.consolidatedContent.races[raceIndex];
    if (!race) return;

    // Extract candidate data from match value
    let candidateName: string;
    let party: string | undefined;
    let official: string | { $create: true } | undefined;
    let incumbent: boolean = false;
    
    if (matchValue.createCandidate?.name) {
      // Creating new candidate
      candidateName = matchValue.createCandidate.name.trim();
      party = matchValue.createCandidate.party;
      official = { $create: true };
    } else if (matchValue.official && typeof matchValue.official === 'string') {
      // Existing official selected
      official = matchValue.official;
      try {
        const officialData = await feathers.getService('officials').get(matchValue.official);
        candidateName = officialData.recentCandidate?.stagingDraft?.name || 
                       officialData.recentCandidate?.name || 
                       matchValue.official; // Fallback to key if no name found
        // Get party and incumbent from official's recent candidate if available
        const recentCandidate = officialData.recentCandidate?.stagingDraft || officialData.recentCandidate;
        if (recentCandidate) {
          const recentCandidateElectionFields = recentCandidate as typeof recentCandidate & RecentCandidateElectionFields;
          party = recentCandidateElectionFields.party;
          incumbent = recentCandidateElectionFields.incumbent || false;
        }
      } catch (error) {
        console.error('Error fetching official:', error);
        candidateName = matchValue.official; // Fallback to key
      }
    } else {
      return; // Invalid match value
    }

    if (!candidateName) return;

    if (!race._id) return;
    const raceId = race._id;
    const optimisticCandidate = {
      name: candidateName,
      party: party || '',
      official,
      incumbent,
      withdrawn: false,
      disqualified: false,
      numberBallots: race.numberBallots ?? 1
    };

    beginTrackedSave();
    setAutoScouter(previous => {
      if (!previous?.consolidatedContent?.races) return previous;
      return {
        ...previous,
        consolidatedContent: {
          ...previous.consolidatedContent,
          races: previous.consolidatedContent.races.map(currentRace => {
            if (currentRace._id !== raceId) return currentRace;
            const candidateExists = (currentRace.candidates || []).some(currentCandidate => (
              (
                typeof official === 'string'
                && currentCandidate.official === official
              )
              || currentCandidate.name === candidateName
            ));
            return candidateExists
              ? currentRace
              : {
                  ...currentRace,
                  candidates: [...(currentRace.candidates || []), optimisticCandidate]
                };
          })
        }
      };
    });
    setNewCandidateMatches(previous => {
      const next = { ...previous };
      delete next[raceIndex];
      return next;
    });

    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $addCandidateToGroup: {
          candidateName,
          rowId: raceId,
          party,
          official,
          incumbent,
          withdrawn: false
        }
      });
      finishTrackedSave(true);
    } catch (error) {
      console.error('Error adding candidate:', error);
      setAutoScouter(previous => {
        if (!previous?.consolidatedContent?.races) return previous;
        return {
          ...previous,
          consolidatedContent: {
            ...previous.consolidatedContent,
            races: previous.consolidatedContent.races.map(currentRace => (
              currentRace._id === raceId
                ? {
                    ...currentRace,
                    candidates: (currentRace.candidates || []).filter(
                      currentCandidate => currentCandidate !== optimisticCandidate
                    )
                  }
                : currentRace
            ))
          }
        };
      });
      setNewCandidateMatches(previous => ({
        ...previous,
        [raceIndex]: matchValue
      }));
      finishTrackedSave(false);
    }
  };

  const openAddContentItemDialog = (contentType: 'race' | 'measure') => {
    setAddContentItemDialog({ contentType, error: '' });
  };

  const handleAddConsolidatedContentItem = async () => {
    if (!addContentItemDialog || addingContentItem) return;
    const { contentType } = addContentItemDialog;
    const inputText = addContentInputRef.current?.value.trim() || '';
    if (!inputText) {
      setAddContentItemDialog(current => current && ({
        ...current,
        error: 'Input text is required.'
      }));
      return;
    }

    const normalizedInputText = inputText.toLocaleLowerCase();
    const existingInputTexts = [
      ...(autoScouter?.consolidatedContent?.races || []),
      ...(autoScouter?.consolidatedContent?.measures || [])
    ].flatMap(item => (
      item.inputTextsUnique?.length ? item.inputTextsUnique : [item.inputText]
    ));
    if (existingInputTexts.some(existing => (
      existing?.trim().toLocaleLowerCase() === normalizedInputText
    ))) {
      setAddContentItemDialog(current => current && ({
        ...current,
        error: `A content item already exists for input text "${inputText}".`
      }));
      return;
    }

    setAddingContentItem(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $addContentItem: {
          contentType,
          inputText
        }
      });
      setAutoScouter(updatedAutoScouter as AutoScouter);
      setAddContentItemDialog(null);
    } catch (error: any) {
      console.error(`Error adding ${contentType}:`, error);
      setAddContentItemDialog(current => current && ({
        ...current,
        error: error?.message
        || error?.error?.message
        || `Failed to add ${contentType}. Please try again.`
      }));
    } finally {
      setAddingContentItem(false);
    }
  };

  const handleAddRace = () => openAddContentItemDialog('race');
  const handleAddMeasure = () => openAddContentItemDialog('measure');

  // Function to fetch full auto-scouter data with all related data
  const fetchFullData = async () => {
    if (!feathers || !autoScouterId) return;
    
    setLoading(true);
    try {
      const autoScouterData = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(autoScouterData);
      
      if (autoScouterData.districtId) {
        try {
          const districtResponse = await feathers.getService('districts').find({
            query: { _id: autoScouterData.districtId }
          });
          const districts = districtResponse.data || (Array.isArray(districtResponse) ? districtResponse : []);
          setDistrict(districts[0] || null);
        } catch (error) {
          console.error('Error fetching district:', error);
        }
      }

      // Fetch offices and districts for each race/measure
      if (autoScouterData.consolidatedContent?.races || autoScouterData.consolidatedContent?.measures) {
        const consolidatedRaces = autoScouterData.consolidatedContent?.races || [];
        const officeKeys = consolidatedRaces
          .map(r => r.office?.officeKey)
          .filter((key): key is string => Boolean(key));
        const raceDistrictIds = consolidatedRaces
          .map(r => r.district?.districtId)
          .filter((id): id is string => Boolean(id));
        const measureDistrictIds = (autoScouterData.consolidatedContent?.measures || [])
          .map(m => m?.matchedData?.districtId || m?.matchedData?.district?._id || m?.matchedData?.district?.districtId)
          .filter((id): id is string => Boolean(id));
        const districtIds = [...new Set([...raceDistrictIds, ...measureDistrictIds])];

        const pageLimit = 20;
        // chunk into chunks of 20
        const officeKeysChunks = [];
        for(let i = 0; i < officeKeys.length; i += pageLimit) {
          officeKeysChunks.push(officeKeys.slice(i, i + pageLimit));
        }
        const districtIdsChunks = [];
        for(let i = 0; i < districtIds.length; i += pageLimit) {
          districtIdsChunks.push(districtIds.slice(i, i + pageLimit));
        }

        const officeResponsesChunky = await Promise.all(officeKeysChunks.map(chunk => 
          feathers.getService('offices').find({
            query: { key: chunk, $limit: pageLimit }
          })
        ));
        const districtResponsesChunky = await Promise.all(districtIdsChunks.map(chunk => 
          feathers.getService('districts').find({
            query: { _id: chunk, $limit: pageLimit }
          })
        ));

        const officeResponses = officeResponsesChunky.flatMap(r => r.data || []);
        const districtResponses = districtResponsesChunky.flatMap(r => r.data || []);

        const officesMap: Record<string, Office> = {};
        officeResponses.forEach((office, index) => {
          if (office && office?.key) {
            officesMap[office.key] = office;
          }
        });
        setOffices(officesMap);

        const districtsMap: Record<string, District> = {};
        districtResponses.forEach((dist: District) => {
          if (dist && dist._id) {
            districtsMap[dist._id] = dist;
          }
        });  
        setRaceDistricts(districtsMap);
      }
    } catch (error) {
      console.error('Error fetching auto-scouter detail:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lightweight function to poll for auto-scouter status updates
  const pollAutoScouter = async () => {
    if (!feathers || !autoScouterId) return;
    
    try {
      const autoScouterData = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(autoScouterData);
    } catch (error) {
      console.error('Error polling auto-scouter:', error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (feathers && autoScouterId && electionKey) {
      fetchFullData();
    }
  }, [feathers, autoScouterId, electionKey]);

  // Load possibleDistricts when available
  useEffect(() => {
    if (!feathers || !autoScouter?.possibleDistricts || autoScouter.possibleDistricts.length === 0) {
      setPossibleDistrictsFull([]);
      return;
    }

    const loadPossibleDistricts = async () => {
      try {
        const numPossibleDistricts = autoScouter.possibleDistricts?.length || 0;
        const pageLimit = 20;
        const numPages = Math.ceil(numPossibleDistricts / pageLimit);
        const possibleDistrictsChunks = [];
        for(let i = 0; i < numPages; i++) {
          possibleDistrictsChunks.push(autoScouter.possibleDistricts?.slice(i * pageLimit, (i + 1) * pageLimit));
        }
        const possibleDistricts = await Promise.all(possibleDistrictsChunks.map(chunk => feathers.getService('districts').find({ query: { _id: chunk } })));
        setPossibleDistrictsFull(possibleDistricts.map(r => r.data || []).flat() as District[]);
      } catch (error) {
        console.error('Error loading possibleDistricts:', error);
      }
    };

    loadPossibleDistricts();
  }, [feathers, autoScouter?.possibleDistricts]);

  // Update markedDistrictsSet when autoScouter changes
  useEffect(() => {
    if (autoScouter?.markedDistricts) {
      setMarkedDistrictsSet(new Set(autoScouter.markedDistricts));
    } else {
      setMarkedDistrictsSet(new Set());
    }
  }, [autoScouter?.markedDistricts]);

  // Load state configuration for humanizing district types
  useEffect(() => {
    if (!feathers || !autoScouter?.stateName) return;

    const loadStateConfiguration = async () => {
      try {
        const stateCode = stateCodeForName(autoScouter.stateName);
        if (stateCode) {
          const config = await feathers.getService('state-configurations').get(stateCode.toLowerCase());
          setStateConfiguration(config);
        }
      } catch (error) {
        console.error('Error loading state configuration:', error);
      }
    };

    loadStateConfiguration();
  }, [feathers, autoScouter?.stateName]);

  // Keep old, invalid, and unavailable step URLs on the furthest available workflow page.
  useEffect(() => {
    if (!enableStepNavigation || !autoScouter) return;

    const urlConfirmed = autoScouter.review?.url === true;
    const contentConfirmed = autoScouter.review?.content === true;
    const matchingConfirmed = autoScouter.review?.matching === true;
    const scoutingConfirmed = autoScouter.review?.scouting === true;
    const dataOperationsReady = matchingConfirmed && scoutingConfirmed;
    const recommendedStep: AutoScouterStepName = dataOperationsReady
      ? 'data-operations'
      : matchingConfirmed
        ? 'scouting-details'
        : contentConfirmed
          ? 'consolidated-content'
          : urlConfirmed
            ? 'scraped-content'
            : 'homepage';
    const stepIsAvailable = activeStepName === 'homepage'
      || (activeStepName === 'scraped-content' && urlConfirmed)
      || (activeStepName === 'consolidated-content' && contentConfirmed)
      || activeStepName === 'scouting-details'
      || (activeStepName === 'data-operations' && dataOperationsReady);

    if (!isAutoScouterStepName(activeStepName) || !stepIsAvailable) {
      history.replace(`${autoScouterBasePath}/${recommendedStep}`);
    }
  }, [
    activeStepName,
    autoScouter,
    autoScouterBasePath,
    enableStepNavigation,
    history
  ]);

  // Trigger full data refresh when status changes to 'completed'
  useEffect(() => {
    if (!autoScouter || !feathers || !autoScouterId) return;

    const status = autoScouter.status;
    if (status === 'completed') {
      // Do a full refresh to get all related data (consolidatedContent, offices, districts, etc.)
      fetchFullData();
    }
  }, [autoScouter?.status]);

  // Build scraped content list from potentialSampleBallots (must be before early returns)
  const scrapedContentItems = useMemo<ScrapedContentItem[]>(() => {
    if (!autoScouter) {
      return [];
    }
    const items = (autoScouter.potentialSampleBallots || []).map(potential => {
      const rawBallot = autoScouter.sampleBallotsRaw?.find(
        raw => raw.url === potential.url
      ) || { url: potential.url };
      
      return {
        url: potential.url,
        parseStatus: potential.parseStatus,
        relevant: potential.relevant,
        rawBallot,
        sourceType: potential.contentType || 'sample-ballot',
        bodyText: potential.bodyText,
        errorMessage: potential.errorMessage,
        warningMessage: potential.warningMessage,
        promptLastUsed: potential.promptLastUsed,
      };
    });
    
    return items.sort((a, b) => {
      if (a.parseStatus === 'error' && b.parseStatus !== 'error') return -1;
      if (a.parseStatus !== 'error' && b.parseStatus === 'error') return 1;
      if (a.relevant === true && b.relevant !== true) return -1;
      if (a.relevant !== true && b.relevant === true) return 1;
      if (a.relevant === false && b.relevant !== false) return -1;
      if (a.relevant !== false && b.relevant === false) return 1;
      return 0;
    });
  }, [autoScouter]);
  
  // Calculate ballot/candidate list counts for the accordion title
  const ballotCounts = useMemo(() => {
    if (!autoScouter) {
      return { total: 0, relevant: 0 };
    }
    
    const total = autoScouter.potentialSampleBallots?.length || 0;
    const relevant = autoScouter.potentialSampleBallots?.filter(p => p.relevant === true).length || 0;
    return { total, relevant };
  }, [autoScouter]);

  // Check if there are no errors in parsed sample ballots
  const hasNoErrors = useMemo(() => {
    if (!scrapedContentItems || scrapedContentItems.length === 0) {
      return false;
    }
    return scrapedContentItems.every(item => item.parseStatus !== 'error');
  }, [scrapedContentItems]);

  // Check if all parse statuses are complete (success only)
  const allParseStatusesComplete = useMemo(() => {
    if (!scrapedContentItems || scrapedContentItems.length === 0) {
      return false;
    }
    return scrapedContentItems.every(item => item.parseStatus === 'success');
  }, [scrapedContentItems]);

  const hasNoPotentialSampleBallots = (autoScouter?.potentialSampleBallots?.length || 0) === 0;
  const canConfirmContent = hasNoPotentialSampleBallots || (hasNoErrors && allParseStatusesComplete);

  const isContentConfirmed = autoScouter?.review?.content === true;
  const isMatchingConfirmed = autoScouter?.review?.matching === true;
  const isScoutingConfirmed = autoScouter?.review?.scouting === true;
  const isDataOperationsReady = isMatchingConfirmed && isScoutingConfirmed;

  // Check if any matchedLabels are in-progress or not-started
  const hasIncompleteMatchedLabels = useMemo(() => {
    const matchedLabels = autoScouter?.matchedLabels;
    if (!matchedLabels) return false;
    
    const races = matchedLabels.races || [];
    const measures = matchedLabels.measures || [];
    
    const allLabels = [...races, ...measures];
    return allLabels.some(label => 
      label.status === 'in-progress' || label.status === 'not-started'
    );
  }, [autoScouter]);

  // The backend process stage is the authoritative cancellable matching state.
  const isMatchingInProgress = useMemo(() => {
    return autoScouter?.processInProgress?.stage === 'matching';
  }, [autoScouter?.processInProgress?.stage]);

  // Check if polling should be active (same logic as the polling useEffect)
  const isPollingActive = useMemo(() => {
    if (!autoScouter) return false;
    const processStage = autoScouter.processInProgress?.stage;
    if (['creating', 'matching', 'rematching-officials'].includes(processStage || '')) {
      return true;
    }
    if (autoScouter.status === 'crawling') {
      return autoScouter.crawlStatus === 'in-progress';
    }
    if (autoScouter.status === 'parsing') {
      return (autoScouter.potentialSampleBallots || []).some(content =>
        ['in-progress', 'not-started'].includes(content.parseStatus)
      );
    }
    return false;
  }, [
    autoScouter?.status,
    autoScouter?.crawlStatus,
    autoScouter?.processInProgress?.stage,
    autoScouter?.potentialSampleBallots
  ]);

  // Poll for updates based on isPollingActive
  useEffect(() => {
    if (!autoScouterId || !feathers || !isPollingActive) return;

    // Poll more frequently for explicitly tracked backend processes
    const hasProcessInProgress = autoScouter?.processInProgress?.stage &&
      autoScouter.processInProgress.stage !== 'none';
    const pollIntervalMs = hasProcessInProgress ? 5000 : 10000;

    const pollInterval = setInterval(() => {
      pollAutoScouter();
    }, pollIntervalMs);

    return () => {
      clearInterval(pollInterval);
    };
  }, [isPollingActive, autoScouter?.processInProgress?.stage, autoScouterId, feathers]);

  // Handler to retrigger entity matching
  const handleRetriggerEntityMatching = async () => {
    if (!autoScouter || !autoScouterId) return;

    try {
      // Check if we have sampleBallotsRaw to retrigger with
      if (!autoScouter.sampleBallotsRaw || autoScouter.sampleBallotsRaw.length === 0) {
        console.error('Cannot retrigger matching: no sampleBallotsRaw available');
        return;
      }

      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $matchBallotContent: { reset: false }
      });

      // Refresh the auto-scouter data to get updated status
      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
    } catch (error) {
      console.error('Error retriggering entity matching:', error);
    }
  };

  const handleCancelEntityMatching = async () => {
    if (
      !autoScouter
      || !autoScouterId
      || cancellingMatching
      || autoScouter.processInProgress?.stage !== 'matching'
    ) return;

    setCancellingMatching(true);
    try {
      const updatedAutoScouter = await feathers.getService('auto-scouter').patch(
        autoScouterId,
        { $cancelMatching: true }
      );
      setAutoScouter(updatedAutoScouter);
    } catch (error: any) {
      console.error('Error cancelling entity matching:', error);
      setErrorMessage(
        error?.message
        || error?.error?.message
        || 'Failed to cancel entity matching. Please try again.'
      );
      setErrorDialogOpen(true);
    } finally {
      setCancellingMatching(false);
    }
  };

  // Handler to re-match ballot content (clears matchedLabels and retriggers)
  const handleRematchBallotContent = async () => {
    if (!autoScouter || !autoScouterId) return;

    setConsolidatedContentMoreMenuAnchorEl(null);

    try {
      // Check if we have sampleBallotsRaw to retrigger with
      if (!autoScouter.sampleBallotsRaw || autoScouter.sampleBallotsRaw.length === 0) {
        console.error('Cannot re-match: no sampleBallotsRaw available');
        return;
      }

      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $matchBallotContent: { reset: true }
      });

      // Refresh the auto-scouter data to get updated status
      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
    } catch (error) {
      console.error('Error re-matching ballot content:', error);
    }
  };

  const handleRematchOfficials = async () => {
    if (!autoScouter || !autoScouterId) return;

    setConsolidatedContentMoreMenuAnchorEl(null);
    setRematchingOfficials(true);
    setAutoScouter(current => current ? {
      ...current,
      processInProgress: {
        stage: 'rematching-officials',
        startedAt: new Date()
      }
    } : current);

    try {
      await feathers.getService('auto-scouter').patch(autoScouterId, {
        $rematchOfficials: true
      });

      const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
      setAutoScouter(updatedAutoScouter);
    } catch (error) {
      console.error('Error re-matching officials:', error);
      try {
        const updatedAutoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
        setAutoScouter(updatedAutoScouter);
      } catch (refreshError) {
        console.error('Error refreshing auto-scouter after re-matching officials failed:', refreshError);
        setAutoScouter(current => current ? {
          ...current,
          processInProgress: {
            stage: 'none',
            startedAt: new Date()
          }
        } : current);
      }
    } finally {
      setRematchingOfficials(false);
    }
  };

  if (loading || !election) {
    return (
      <Wrapper>
        <LoadingTile>
          <span>Loading auto-scouter details...</span>
          <LoadingSpinner width={64} />
        </LoadingTile>
      </Wrapper>
    );
  }

  if (!autoScouter) {
    return (
      <Wrapper>
        <Typography variant="body1" color="textSecondary">
          Auto-scouter not found.
        </Typography>
      </Wrapper>
    );
  }

  const districtDisplay = district?.longName || district?.name || autoScouter.districtId || 'Unknown';
  const targetContentType = 'Sample Ballots + Candidate Lists';
  const electionUrl = autoScouter.url;
  const isUrlConfirmed = autoScouter.review?.url === true;
  const displayedStepName: AutoScouterStepName = isAutoScouterStepName(activeStepName)
    ? activeStepName
    : 'homepage';
  const currentStepIndex = AUTO_SCOUTER_STEPS.findIndex(step => step.routeName === displayedStepName);
  const workflowSteps = [
    {
      name: AUTO_SCOUTER_STEPS[0].name,
      isComplete: isUrlConfirmed,
      disabled: false,
      onClick: () => navigateToStep('homepage')
    },
    {
      name: AUTO_SCOUTER_STEPS[1].name,
      isComplete: isContentConfirmed,
      disabled: !isUrlConfirmed,
      onClick: () => navigateToStep('scraped-content')
    },
    {
      name: AUTO_SCOUTER_STEPS[2].name,
      isComplete: autoScouter.review?.matching === true,
      disabled: !isContentConfirmed,
      onClick: () => navigateToStep('consolidated-content')
    },
    {
      name: AUTO_SCOUTER_STEPS[3].name,
      isComplete: autoScouter.review?.scouting === true,
      disabled: false,
      onClick: () => navigateToStep('scouting-details')
    },
    {
      name: AUTO_SCOUTER_STEPS[4].name,
      isComplete: autoScouter.createStatus === 'success',
      disabled: !isDataOperationsReady,
      onClick: () => navigateToStep('data-operations')
    }
  ];

  return (
    <Wrapper>
      {showToolbar && (
        <DataDetailToolbar
          style={{ marginBottom: '24px' }}
          onBack={{ to: { pathname: `/elections/${electionKey}/auto-scouters` }}}
          navTree={[
            {
              text: 'Elections',
              to: { pathname: `/elections` }
            },
            {
              text: electionData?.name || election.name || 'Election',
              to: { pathname: `/elections/${electionKey}` }
            },
            {
              text: 'Auto Scouters',
              to: { pathname: `/elections/${electionKey}/auto-scouters` }
            },
            { text: districtDisplay }
          ]}
          navigationTrailingComponent={enableStepNavigation ? (
            <StepTracker
              steps={workflowSteps}
              currentStep={currentStepIndex}
              compact
              style={{ width: '540px', maxWidth: '50vw', paddingRight: '80px' }}
            />
          ) : undefined}
          actionButtonsComponent={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isPollingActive && (
                <CircularProgress size={20} />
              )}
              <IconButton
                onClick={(event) => setMoreMenuAnchorEl(event.currentTarget)}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={moreMenuAnchorEl}
                open={Boolean(moreMenuAnchorEl)}
                onClose={() => setMoreMenuAnchorEl(null)}
              >
                <MenuItem
                  onClick={handleDelete}
                  disabled={isEditingDisabled}
                >
                  Delete auto-scouter
                </MenuItem>
              </Menu>
            </Box>
          }
        />
      )}

      {autoScouter.matchStatus === 'error' && (
        <Alert
          severity="error"
          sx={{ marginBottom: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetriggerEntityMatching}>
              Retry matching
            </Button>
          }
        >
          Entity matching failed. One or more race or measure labels could not be matched.
        </Alert>
      )}

      {(!enableStepNavigation || displayedStepName === 'homepage') && (
      <SummarySection>
        <DistrictRow>
          <DistrictInfo>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {districtDisplay}
              </Typography>
              {autoScouter.createStatus === 'success' && (
                <Tooltip title="All data operations completed">
                  <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '24px' }} />
                </Tooltip>
              )}
              <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.875rem' }}>
                {targetContentType}
              </Typography>
              {autoScouter.crawlStatus === 'in-progress' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="Crawling webpage">
                    <span>
                      <CircularProgress size={16} />
                    </span>
                  </Tooltip>
                  <Tooltip title={cancellingCrawl ? 'Cancelling crawl...' : 'Cancel crawl'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleCancelCrawl}
                        disabled={cancellingCrawl}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
              {autoScouter.crawlStatus !== 'in-progress' && (
                <Tooltip title="Re-crawl content">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleOpenRecrawlDialog}
                      disabled={recrawling}
                    >
                      {recrawling ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
              Requested At {autoScouter.requestedAt 
                ? moment(autoScouter.requestedAt instanceof Date ? autoScouter.requestedAt : new Date(autoScouter.requestedAt)).format('MMM D, YYYY h:mm A')
                : '--'
              }
              {autoScouter.finishedAt && (
                <> • Finished {moment(autoScouter.finishedAt instanceof Date ? autoScouter.finishedAt : new Date(autoScouter.finishedAt)).format('MMM D, YYYY h:mm A')}</>
              )}
            </Typography>
          </DistrictInfo>
        </DistrictRow>
        
        <ElectionHomepageRow>
          <ElectionHomepageContent>
            <SummaryRow style={{ flex: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Found election homepage
              </Typography>
              {editingUrl ? (
                <TextField
                  value={editedUrl}
                  onChange={(e) => setEditedUrl(e.target.value)}
                  placeholder="Enter election homepage URL"
                  fullWidth
                  size="small"
                  disabled={savingUrl || isEditingDisabled}
                  style={{ marginTop: '4px' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleSaveUrl}
                          disabled={savingUrl || !editedUrl.trim() || isEditingDisabled}
                          color="primary"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEditUrl}
                          disabled={savingUrl || isEditingDisabled}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '4px' }}>
                  {electionUrl ? (
                    <Typography variant="body1">
                      <a href={electionUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#22034F' }}>
                        {electionUrl}
                      </a>
                    </Typography>
                  ) : (
                    <Typography variant="body1" color="textSecondary">
                      --
                    </Typography>
                  )}
                  {autoScouter.crawlStatus !== 'in-progress' && (
                    <IconButton
                      size="small"
                      onClick={handleStartEditUrl}
                      style={{ padding: '4px' }}
                      disabled={isEditingDisabled}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
            </SummaryRow>
            {!isUrlConfirmed && !editingUrl && autoScouter.crawlStatus !== 'in-progress' && Boolean(electionUrl) && (
              <ElectionHomepageActions>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmUrl}
                  size="small"
                  disabled={confirmingUrl}
                >
                  {confirmingUrl ? 'Confirming...' : 'Confirm'}
                </Button>
              </ElectionHomepageActions>
            )}
          </ElectionHomepageContent>
        </ElectionHomepageRow>
      </SummarySection>
      )}

      {(!enableStepNavigation || displayedStepName === 'scraped-content') && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '36px', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                Scraped content
              </Typography>
              {ballotCounts.total > 0 && (
                <Typography variant="body2" color="textSecondary">
                  ({ballotCounts.total} item{ballotCounts.total !== 1 ? 's' : ''} found, {ballotCounts.relevant} relevant)
                </Typography>
              )}
              {autoScouter?.review?.url && (
                <CheckCircleIcon style={{ fontSize: '20px', color: '#4caf50' }} />
              )}
              {scrapedContentItems.length > 0 && (
                <Tooltip title="Bulk edit">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBulkEditMode(!bulkEditMode);
                      if (bulkEditMode) {
                        setSelectedItems(new Set());
                      }
                    }}
                    disabled={isEditingDisabled}
                    style={{ marginLeft: '0' }}
                  >
                    <EditIcon style={{ fontSize: '20px' }}/>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box sx={{ pt: 0.5 }}>
            {scrapedContentItems.length === 0 ? (
              <Box style={{ padding: '16px' }}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                  No scraped content found.
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddContent}
                    disabled={isEditingDisabled}
                    style={{
                      borderColor: '#E0E0E0',
                      color: '#313639',
                      textTransform: 'none'
                    }}
                  >
                    Add Content
                  </Button>
                  {canConfirmContent && !isContentConfirmed && (
                    <Button
                      variant="contained"
                      onClick={handleConfirmContent}
                      disabled={confirmingContent}
                      style={{
                        backgroundColor: '#22034F',
                        color: '#FFFFFF',
                        minWidth: '200px'
                      }}
                    >
                      {confirmingContent ? 'Confirming...' : 'Confirm & Continue'}
                    </Button>
                  )}
                </Box>
              </Box>
            ) : (
              <>
                {bulkEditMode && (
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    gap={2} 
                    mb={2}
                    sx={{ 
                    }}
                  >
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={selectedItems.size === 0 || bulkUpdating || isEditingDisabled}
                      onClick={handleBulkMarkAsNotRelevant}
                    >
                      {bulkUpdating ? 'Updating...' : 'Mark as Not Relevant'}
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setBulkEditMode(false);
                        setSelectedItems(new Set());
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
                <ScrapedContentList>
                  {scrapedContentItems.map((item, index) => (
                    <ParsedSampleBallotTile
                      key={item.url}
                      url={item.url}
                      parseStatus={item.parseStatus}
                      value={item.rawBallot}
                      relevant={item.relevant}
                      enableEditing={true}
                      disableEditing={isEditingDisabled}
                      autoScouterId={autoScouterId}
                      sourceType={item.sourceType}
                      bodyText={item?.bodyText}
                      errorMessage={item.errorMessage}
                      warningMessage={item.warningMessage}
                      promptLastUsed={item.promptLastUsed}
                      partyOptions={partyOptions}
                      bulkEditMode={bulkEditMode}
                      isSelected={selectedItems.has(item.url)}
                      removing={removingContentUrls.has(item.url)}
                      onRemove={() => handleRemoveContent(item)}
                      onSelectionChange={(selected: boolean) => {
                        setSelectedItems(prev => {
                          const next = new Set(prev);
                          if (selected) {
                            next.add(item.url);
                          } else {
                            next.delete(item.url);
                          }
                          return next;
                        });
                      }}
                      onChangeBallot={(updatedRaw: SampleBallotRaw | null, updatedPotential: PotentialSampleBallot | null) => {
                        setAutoScouter(prev => {
                          if (!prev) return prev;

                          const next: AutoScouter = { ...prev };

                          if (updatedRaw) {
                            const rawList = [...(next.sampleBallotsRaw || [])];
                            const rawIndex = rawList.findIndex(r => r.url === updatedRaw.url);
                            if (rawIndex >= 0) {
                              rawList[rawIndex] = updatedRaw;
                            } else {
                              rawList.push(updatedRaw);
                            }
                            next.sampleBallotsRaw = rawList;
                          }

                          if (updatedPotential) {
                            const potList = [...(next.potentialSampleBallots || [])];
                            const potIndex = potList.findIndex(
                              p => p.url === updatedPotential.url && p.contentType === updatedPotential.contentType
                            );
                            if (potIndex >= 0) {
                              potList[potIndex] = updatedPotential;
                            } else {
                              potList.push(updatedPotential);
                            }
                            next.potentialSampleBallots = potList;
                          }

                          return next;
                        });
                      }}
                      onChangeAutoScouter={(updatedAutoScouter: AutoScouter) => {
                        // Update the full AutoScouter state with the server response
                        setAutoScouter(updatedAutoScouter);
                        // The polling logic will automatically pick up changes if isPollingActive is true
                        // Entity matching may have been triggered, so polling will catch those updates
                      }}
                    />
                  ))}
                </ScrapedContentList>
                <Box display="flex" mt={2} pt={2} borderTop="1px solid #EEEEEE">
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddContent}
                    disabled={isEditingDisabled}
                    style={{
                      borderColor: '#E0E0E0',
                      color: '#313639',
                      textTransform: 'none'
                    }}
                  >
                    Add Content
                  </Button>
                </Box>
                {canConfirmContent && !isContentConfirmed && (
                  <Box display="flex" justifyContent="flex-end" mt={2} pt={2} borderTop="1px solid #EEEEEE">
                    <Button
                      variant="contained"
                      onClick={handleConfirmContent}
                      disabled={confirmingContent}
                      style={{ 
                        backgroundColor: '#22034F', 
                        color: '#FFFFFF',
                        minWidth: '200px'
                      }}
                    >
                      {confirmingContent ? 'Confirming...' : 'Confirm & Continue'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
      </Box>
      )}

      {(!enableStepNavigation || displayedStepName === 'consolidated-content') && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '36px', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                Consolidated Content
              </Typography>
              {autoScouter?.review?.matching && (
                <CheckCircleIcon style={{ fontSize: '20px', color: '#4caf50' }} />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', flexShrink: 0 }}>
                {isRematchingOfficials && (
                  <>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="textSecondary">
                      Re-matching officials...
                    </Typography>
                  </>
                )}
                {isMatchingInProgress && (
                  <>
                    <CircularProgress size={16} />
                    <Tooltip title={cancellingMatching ? 'Cancelling entity matching' : 'Cancel entity matching'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelEntityMatching();
                        }}
                        disabled={cancellingMatching}
                        sx={{ padding: '4px' }}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConsolidatedContentMoreMenuAnchorEl(e.currentTarget);
                  }}
                  sx={{ padding: '4px' }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={consolidatedContentMoreMenuAnchorEl}
                  open={Boolean(consolidatedContentMoreMenuAnchorEl)}
                  onClose={() => setConsolidatedContentMoreMenuAnchorEl(null)}
                >
                  <MenuItem 
                    onClick={handleRematchBallotContent}
                    disabled={isEditingDisabled || !autoScouter?.sampleBallotsRaw || autoScouter.sampleBallotsRaw.length === 0}
                  >
                    Re-match ballot content
                  </MenuItem>
                  <MenuItem
                    onClick={handleRematchOfficials}
                    disabled={isEditingDisabled || isRematchingOfficials || races.length === 0}
                  >
                    Re-match officials
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Box>
          <Box>
            <Content>
        {(races.length > 0 || measures.length > 0) && (
          <ProgressSection>
            <ProgressInfo>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  Review Progress
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {reviewProgress.completed} of {reviewProgress.total} reviewed
                </Typography>
                <Button
                  size="small"
                  variant={consolidatedContentFilter === 'needs-review' ? 'contained' : 'outlined'}
                  startIcon={<FilterListIcon />}
                  aria-pressed={consolidatedContentFilter === 'needs-review'}
                  onClick={() => setConsolidatedContentFilter(current => current === 'needs-review' ? 'all' : 'needs-review')}
                  sx={{
                    borderRadius: '9999px',
                    borderColor: consolidatedContentFilter === 'needs-review' ? 'primary.main' : 'divider',
                    backgroundColor: consolidatedContentFilter === 'needs-review' ? 'primary.main' : 'common.white',
                    color: consolidatedContentFilter === 'needs-review' ? 'primary.contrastText' : 'text.primary',
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontWeight: 400,
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: consolidatedContentFilter === 'needs-review' ? 'primary.dark' : 'primary.light',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Need Review
                </Button>
              </Box>
            </ProgressInfo>
            <ProgressBar 
              percentage={reviewProgress.percentage}
              height={8}
              backgroundColor="#E0E0E0"
              color="#4CAF50"
            />
          </ProgressSection>
        )}

        <StickySectionHeader>
        <SectionToolbar>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Races
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedRaceIndices.size > 0 && (
              <>
                <Typography variant="body2" color="textSecondary">
                  {selectedRaceIndices.size} selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleBulkDeleteRaces}
                  disabled={bulkUpdatingRaces || !isContentConfirmed || isEditingDisabled}
                >
                  Delete
                </Button>
                {selectedRacesHaveRaceParty && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleBulkUnsetRaceParty}
                    disabled={bulkUpdatingRaces || !isContentConfirmed || isEditingDisabled}
                  >
                    Unset race party
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MergeTypeIcon />}
                  onClick={handleGroupSelectedRaces}
                  disabled={selectedRaceIndices.size < 2 || bulkUpdatingRaces || !isContentConfirmed || isEditingDisabled}
                >
                  Group rows
                </Button>
              </>
            )}
            {duplicateRaceFilter && (
              <Button
                size="small"
                variant="contained"
                endIcon={<CloseIcon />}
                aria-label={`Clear duplicate filter for ${duplicateRaceFilter.inputText}`}
                onClick={() => setDuplicateRaceFilter(null)}
                sx={{
                  borderRadius: '9999px',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontWeight: 400,
                }}
              >
                Duplicate to &quot;{duplicateRaceFilter.inputText}&quot;
              </Button>
            )}
            {selectedRaceIndices.size === 0 && isContentConfirmed && !isEditingDisabled && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddRace}
                disabled={addingContentItem}
              >
                Add race
              </Button>
            )}
          </Box>
        </SectionToolbar>
        <TableHeader style={{ gridTemplateColumns: showRacePartyColumn ? '78px 2fr 1fr 1fr 80px 1fr 60px 100px 80px 88px' : '78px 2fr 1fr 1fr 80px 1fr 60px 100px 88px' }}>
          <Checkbox
            size="small"
            checked={allFilteredRacesSelected}
            indeterminate={!allFilteredRacesSelected && someFilteredRacesSelected}
            inputProps={{ 'aria-label': 'select all visible races' }}
            onChange={(event) => {
              setSelectedRaceIndices(previous => {
                const next = new Set(previous);
                selectableFilteredRaces.forEach(({ index }) => {
                  if (event.target.checked) {
                    next.add(index);
                  } else {
                    next.delete(index);
                  }
                });
                return next;
              });
            }}
            disabled={selectableFilteredRaces.length === 0 || bulkUpdatingRaces || !isContentConfirmed || isEditingDisabled}
            sx={{ padding: 0, justifySelf: 'center' }}
          />
          <Typography variant="h5">Input Text</Typography>
          <Typography variant="h5">Office</Typography>
          <Typography variant="h5">District</Typography>
          <Typography variant="h5">Special</Typography>
          <Typography variant="h5">Position</Typography>
          <Typography variant="h5">Plurality</Typography>
          <div></div>
          {showRacePartyColumn && <Typography variant="h5">Race Party</Typography>}
          <div></div>
        </TableHeader>
        </StickySectionHeader>

        {filteredRaceRows.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              {duplicateRaceFilter
                ? `No duplicate races found for "${duplicateRaceFilter.inputText}".`
                : consolidatedContentFilter === 'needs-review'
                ? 'No races need review.'
                : 'No races found in consolidated content.'}
            </Typography>
          </div>
        ) : (
          filteredRaceRows.map(({ race, index }) => {
            const office = race.office?.officeKey ? offices[race.office.officeKey] : null;
            const raceDistrict = race.district?.districtId ? raceDistricts[race.district.districtId] : null;
            const needsAttentionForRace = needsAttention(race, index);
            const isExpanded = Boolean(race._id && expandedRaces.has(race._id));
            const candidates = race.candidates || [];
            const candidateCount = candidates.length;
            // Removed - using CandidateMatchCell instead

            return (
              <React.Fragment key={race._id || index}>
                <TableRow style={{
                  gridTemplateColumns: showRacePartyColumn ? '78px 2fr 1fr 1fr 80px 1fr 60px 100px 80px 88px' : '78px 2fr 1fr 1fr 80px 1fr 60px 100px 88px',
                  opacity: race.removed ? 0.45 : 1,
                  pointerEvents: race.removed ? 'none' : 'auto'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                    <Checkbox
                      size="small"
                      checked={selectedRaceIndices.has(index)}
                      inputProps={{ 'aria-label': `select race ${race.inputText || index + 1}` }}
                      onChange={(event) => {
                        setSelectedRaceIndices(previous => {
                          const next = new Set(previous);
                          if (event.target.checked) {
                            next.add(index);
                          } else {
                            next.delete(index);
                          }
                          return next;
                        });
                      }}
                      disabled={race.removed || bulkUpdatingRaces || !isContentConfirmed || isEditingDisabled}
                      sx={{ padding: 0 }}
                    />
                    {!race.removed && needsAttentionForRace && (
                      <Tooltip 
                        title={
                          (() => {
                            const warningMsg = getWarningMessage(race.warnings);
                            const isUnlabeled = !race.office?.officeKey || !race.district?.districtId;
                            const isDuplicate = duplicateGroups.has(index);
                            const missingPlurality = race.position?.plurality === undefined || race.position?.plurality === null;
                            const isPartisanElection = election?.partisan === true;
                            const isNonPartisanElection = election?.partisan === false;
                            const missingRaceParty = isPartisanElection && (!race.raceParty || race.raceParty.trim() === '');
                            const hasRacePartyWhenNonPartisan = isNonPartisanElection && race.raceParty && race.raceParty.trim() !== '';
                            const hasCandidateMissingParty = isNonPartisanElection && race.candidates && race.candidates.some(
                              candidate => !candidate.party || candidate.party.trim() === ''
                            );
                            
                            const messages = [];
                            if (isDuplicate) {
                              messages.push('Duplicate race detected');
                            }
                            if (warningMsg) {
                              messages.push(warningMsg);
                            }
                            if (isUnlabeled) {
                              messages.push('Needs labeling');
                            }
                            if (missingPlurality) {
                              messages.push('Missing plurality');
                            }
                            if (missingRaceParty) {
                              messages.push('Missing raceParty');
                            }
                            if (hasRacePartyWhenNonPartisan) {
                              messages.push('Has raceParty but election is non-partisan');
                            }
                            if (hasCandidateMissingParty) {
                              messages.push('Candidate(s) missing party');
                            }
                            
                            return messages.length > 0 ? messages.join('. ') : 'Race needs attention';
                          })()
                        }
                        arrow
                      >
                        <WarningIcon style={{ color: '#FF9800', fontSize: '20px' }} />
                      </Tooltip>
                    )}
                    {!race.removed && duplicateGroups.has(index) && (
                      <Tooltip title={`Show rows duplicate to "${race.inputText || 'this race'}"`}>
                        <IconButton
                          size="small"
                          aria-label={`Show rows duplicate to ${race.inputText || 'this race'}`}
                          onClick={() => {
                            const key = getDuplicateKey(race);
                            if (key) {
                              setSelectedRaceIndices(new Set());
                              setDuplicateRaceFilter({
                                key,
                                inputText: race.inputText || 'this race'
                              });
                            }
                          }}
                          sx={{ padding: '2px' }}
                        >
                          <SearchIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                  <Typography variant="body1">
                    {race.inputText || '--'}
                  </Typography>
                  <OfficeCell
                    office={office}
                    suggestCreationAs={race.office?.createWithTitle}
                    districtId={election?.state ?? ''}
                    district={null}
                    confidence={race.office?.confidence ?? undefined}
                    onOfficeChange={(newOffice: Office) => handleOfficeChange(index, newOffice, race.office?.confidence || 1.0)}
                    onOfficeClear={() => handleMatchedLabelChange(index, 'office', null)}
                    onChangeConfidence={(confidence: number) => handleOfficeConfidenceChange(index, confidence)}
                    disableEditing={!isContentConfirmed || isEditingDisabled}
                    suggestIssuesToCover
                    showDefinedOfficeActions
                  />
                  <DistrictCell
                    district={raceDistrict || null}
                    suggestCreationAs={race.district?.createWithData}
                    state={election?.state || ''}
                    stateConfiguration={stateConfiguration}
                    confidence={race.district?.confidence ?? undefined}
                    limitTypes={office ? office.districtTypes : undefined}
                    onDistrictChange={(newDistrict: District) => handleDistrictChange(index, newDistrict, race.district?.confidence || 1.0)}
                    onDistrictClear={() => handleMatchedLabelChange(index, 'district', null)}
                    onChangeConfidence={(confidence: number) => handleDistrictConfidenceChange(index, confidence)}
                    disableEditing={!isContentConfirmed || isEditingDisabled}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={race.position?.special || false}
                          onChange={(e) => handleSpecialElectionChange(index, e.target.checked)}
                          color="primary"
                          size="small"
                          disabled={!isContentConfirmed || isEditingDisabled}
                        />
                      }
                      label=""
                      style={{ margin: 0 }}
                    />
                  </div>
                  {isContentConfirmed && !isEditingDisabled ? (
                    <SeatIdentifierCell
                      seatIdentifier={race.position?.seatIdentifier ?? undefined}
                      seatIdentifierType={race.position?.seatIdentifierType ?? undefined}
                      onSeatIdentifierChange={(seatIdentifier: string | null, seatIdentifierType: string | null) => 
                        handleSeatIdentifierChange(index, seatIdentifier, seatIdentifierType)
                      }
                    />
                  ) : (
                    <Typography variant="body2">
                      {race.position?.seatIdentifier ? `Seat ${race.position.seatIdentifier}` : '-'}
                    </Typography>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    {isContentConfirmed && !isEditingDisabled ? (
                      <FormControl size="small" sx={{ minWidth: 60 }}>
                        <Select
                          value={race.position?.plurality !== undefined && race.position?.plurality !== null ? race.position.plurality : ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? null : Number(e.target.value);
                            handlePluralityChange(index, value);
                          }}
                          disabled={isEditingDisabled}
                          displayEmpty
                          sx={{
                            backgroundColor: 'white',
                            '& .MuiSelect-select': {
                              padding: '4px 24px 4px 8px',
                              fontSize: '0.875rem'
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>-</em>
                          </MenuItem>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30].map((num) => (
                            <MenuItem key={num} value={num}>
                              {num}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body2">
                        {race.position?.plurality !== undefined && race.position?.plurality !== null ? race.position.plurality : '-'}
                      </Typography>
                    )}
                    {saving[`${index}-plurality`] && (
                      <CircularProgress size={16} />
                    )}
                  </div>
                  <Typography variant="body2" color="textSecondary">
                    {race.numberBallots !== undefined
                      ? `in ${race.numberBallots ?? 0} ballot${(race.numberBallots ?? 0) !== 1 ? 's' : ''}`
                      : '-'}
                  </Typography>
                  {showRacePartyColumn && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      {isContentConfirmed && !isEditingDisabled ? (
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={race.raceParty || ''}
                            onChange={(e) => {
                              const value = e.target.value === '' ? null : e.target.value;
                              handleRacePartyChange(index, value);
                            }}
                            disabled={isEditingDisabled}
                            displayEmpty
                            sx={{
                              backgroundColor: 'white',
                              '& .MuiSelect-select': {
                                padding: '4px 24px 4px 8px',
                                fontSize: '0.75rem'
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>Unset race party</em>
                            </MenuItem>
                            {partyOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body2" style={{ fontSize: '0.75rem', color: '#666' }}>
                          {race.raceParty || '-'}
                        </Typography>
                      )}
                      {saving[`${index}-raceParty`] && (
                        <CircularProgress size={16} />
                      )}
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    pointerEvents: 'auto'
                  }}>
                    {!race.removed && (race.inputTextsUnique?.length || 0) > 1 && (
                      <Tooltip title="Split grouped row">
                        <IconButton
                          aria-label="split grouped row"
                          size="small"
                          onClick={() => handleSplitRace(index)}
                          disabled={
                            !isContentConfirmed
                            || isEditingDisabled
                            || Boolean(race._id && splittingContentItemIds.has(race._id))
                          }
                        >
                          {race._id && splittingContentItemIds.has(race._id)
                            ? <CircularProgress size={18} />
                            : <CallSplitIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={race.removed ? 'Restore row' : 'Remove row'}>
                      <IconButton
                        aria-label={race.removed ? 'restore row' : 'remove row'}
                        size="small"
                        onClick={() => handleRemoveRace(index, !race.removed)}
                        disabled={!isContentConfirmed || isEditingDisabled}
                      >
                        {race.removed
                          ? <RestoreFromTrashIcon fontSize="small" />
                          : <DeleteOutlineIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableRow>
                <CandidatesSection style={{
                  opacity: race.removed ? 0.45 : 1,
                  pointerEvents: race.removed ? 'none' : 'auto'
                }}>
                  <CandidatesHeader onClick={() => toggleRaceExpansion(race._id)}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    <Typography variant="body2" color="textSecondary">
                      {candidateCount} candidate{candidateCount !== 1 ? 's' : ''}
                    </Typography>
                    {!race.removed && race.warnings?.includes('different-candidates') && (
                      <div 
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip 
                          title="Some candidates don't appear in every ballot. This may indicate missing candidates in some ballots or data inconsistencies."
                          arrow
                        >
                          <InfoOutlinedIcon style={{ fontSize: '16px', color: '#666', cursor: 'help' }} />
                        </Tooltip>
                        <Tooltip title="Accept that candidates may appear in different numbers of ballots">
                          <IconButton
                            size="small"
                            onClick={() => handleSuppressDifferentCandidatesWarning(index)}
                            style={{ padding: '2px' }}
                            disabled={!isContentConfirmed || isEditingDisabled}
                          >
                            <CheckIcon style={{ fontSize: '16px', color: '#4CAF50' }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                  </CandidatesHeader>
                  {isExpanded && (
                    <CandidatesContent>
                      {candidates.map((candidate, candidateIndex) => {
                        // Check if creating new:
                        // 1. official is an object with $create: true, OR
                        // 2. candidate has a name but no official key (official is not a string)
                        const hasOfficialKey = candidate.official && typeof candidate.official === 'string';
                        const isExplicitCreate = isCreateOfficialMarker(candidate.official);
                        const isCreatingNew = isExplicitCreate || (candidate.name && !hasOfficialKey);
                        
                        // Use a more stable key - prefer official key if available, otherwise use name + index
                        const candidateKey = hasOfficialKey
                          ? `official_${candidate.official}` 
                          : `name_${candidate.name || ''}_${candidateIndex}`;
                        
                        // Derive createCandidate from candidate.name and candidate.party when creating new
                        const candidateOfficial = isCreatingNew 
                          ? { $create: true } as { $create: true }
                          : (hasOfficialKey && typeof candidate.official === 'string' ? candidate.official : undefined);
                        const candidateCreateCandidate = isCreatingNew && candidate.name
                          ? {
                              name: candidate.name,
                              party: candidate.party
                            }
                          : undefined;
                        const metadataFields = getCandidateMetadataFields(candidate);
                        const metadataCount = metadataFields.length;
                        const metadataKey = `${index}-${candidateKey}`;
                        const isMetadataExpanded = Boolean(expandedCandidateMetadata[metadataKey]);
                        
                        return (
                          <CandidateRow key={candidateKey}>
                            <CandidateInfo>
                              <Box display="flex" flexDirection="column" gap={0.25}>
                                <CandidateMatchCell
                                  official={candidateOfficial}
                                  createCandidate={candidateCreateCandidate}
                                  seedSearchText={candidate.name}
                                  onChange={(value) => handleCandidateMatchChange(index, candidateIndex, value)}
                                  office={race.office}
                                  district={race.district}
                                  partyOptions={partyOptions}
                                  disabled={!isContentConfirmed || isEditingDisabled}
                                  showCreateCandidateParty={false}
                                />
                                {metadataCount > 0 && (
                                  <>
                                    <Box
                                      component="button"
                                      type="button"
                                      aria-expanded={isMetadataExpanded}
                                      onClick={() => {
                                        setExpandedCandidateMetadata(previous => ({
                                          ...previous,
                                          [metadataKey]: !previous[metadataKey]
                                        }));
                                      }}
                                      style={{
                                        alignItems: 'center',
                                        alignSelf: 'flex-start',
                                        background: 'none',
                                        border: 0,
                                        color: 'rgba(0, 0, 0, 0.6)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        font: 'inherit',
                                        fontSize: '0.68rem',
                                        padding: 0
                                      }}
                                    >
                                      {isMetadataExpanded
                                        ? <ExpandLessIcon style={{ fontSize: '0.9rem' }} />
                                        : <ExpandMoreIcon style={{ fontSize: '0.9rem' }} />}
                                      {metadataCount} metadata field{metadataCount !== 1 ? 's' : ''}
                                    </Box>
                                    <Collapse in={isMetadataExpanded} timeout="auto" unmountOnExit>
                                      <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={0.25}
                                        mt={0.5}
                                        p={1}
                                        style={{ backgroundColor: '#F5F5F5', borderRadius: '4px' }}
                                      >
                                        {metadataFields.map(field => (
                                          <Typography
                                            key={field.label}
                                            variant="caption"
                                            style={{ fontSize: '0.68rem', overflowWrap: 'anywhere' }}
                                          >
                                            <strong>{field.label}:</strong> {field.value}
                                          </Typography>
                                        ))}
                                      </Box>
                                    </Collapse>
                                  </>
                                )}
                              </Box>
                            </CandidateInfo>
                            <CandidateBallotCount>
                              {candidate.numberBallots !== undefined && race.numberBallots !== undefined && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  {candidate.numberBallots ?? 0} of {race.numberBallots ?? 0} ballot{(race.numberBallots ?? 0) !== 1 ? 's' : ''}
                                </Typography>
                              )}
                            </CandidateBallotCount>
                            <CandidateParty>
                              <TextField
                                select
                                size="small"
                                variant="standard"
                                value={candidate.party || ''}
                                onChange={(e) => handleCandidatePartyChange(index, candidateIndex, e.target.value)}
                                disabled={!isContentConfirmed || isEditingDisabled}
                                sx={{ minWidth: 120 }}
                              >
                                <MenuItem value="">None</MenuItem>
                                {partyOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <Typography variant="body2" color="textSecondary">
                                {candidate.incumbent ? 'Incumbent' : ''}
                              </Typography>
                            </CandidateParty>
                            <CandidateActions>
                              <Tooltip title="Remove candidate">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveCandidate(index, candidateIndex)}
                                  disabled={!isContentConfirmed || isEditingDisabled}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </CandidateActions>
                          </CandidateRow>
                        );
                      })}
                      {isContentConfirmed && !isEditingDisabled && (
                        <AddCandidateRow>
                          <CandidateMatchCell
                            style={{ minWidth: 0, width: '100%' }}
                            official={newCandidateMatches[index]?.official}
                            createCandidate={newCandidateMatches[index]?.createCandidate}
                            partyOptions={partyOptions}
                            onChange={(value) => {
                              // When a value is selected (either official key or createCandidate with name), add the candidate
                              const hasOfficial = value.official && (typeof value.official === 'string' || (typeof value.official === 'object' && value.official.$create === true));
                              const hasCreateCandidate = value.createCandidate && value.createCandidate.name;
                              
                              if (hasOfficial || hasCreateCandidate) {
                                handleAddCandidate(index, value);
                              } else {
                                // Update the match value state while user is typing/selecting
                                setNewCandidateMatches(prev => ({ ...prev, [index]: value }));
                              }
                            }}
                            office={race.office}
                            district={race.district}
                            disabled={isEditingDisabled}
                            placeholder="Add new candidate"
                            clearAfterSelect
                          />
                        </AddCandidateRow>
                      )}
                    </CandidatesContent>
                  )}
                </CandidatesSection>
              </React.Fragment>
            );
          })
        )}
        <div style={{
          borderTop: '1px solid #EEEEEE',
          marginTop: '8px',
          display: duplicateRaceFilter ? 'none' : 'block'
        }}>
          <StickySectionHeader>
          <SectionToolbar>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Measures
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedMeasureIndices.size > 0 && (
                <>
                <Typography variant="body2" color="textSecondary">
                  {selectedMeasureIndices.size} selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MergeTypeIcon />}
                  onClick={handleGroupSelectedMeasures}
                  disabled={selectedMeasureIndices.size < 2 || bulkUpdatingMeasures || !isContentConfirmed || isEditingDisabled}
                >
                  Group rows
                </Button>
                </>
              )}
              {selectedMeasureIndices.size === 0 && isContentConfirmed && !isEditingDisabled && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddMeasure}
                  disabled={addingContentItem}
                >
                  Add measure
                </Button>
              )}
            </Box>
          </SectionToolbar>
          <TableHeader style={{ gridTemplateColumns: '84px 2fr 2fr 1.5fr 100px 88px' }}>
            <Checkbox
              size="small"
              checked={allFilteredMeasuresSelected}
              indeterminate={!allFilteredMeasuresSelected && someFilteredMeasuresSelected}
              inputProps={{ 'aria-label': 'select all visible measures' }}
              onChange={(event) => {
                setSelectedMeasureIndices(previous => {
                  const next = new Set(previous);
                  selectableFilteredMeasures.forEach(({ index }) => {
                    if (event.target.checked) {
                      next.add(index);
                    } else {
                      next.delete(index);
                    }
                  });
                  return next;
                });
              }}
              disabled={selectableFilteredMeasures.length === 0 || bulkUpdatingMeasures || !isContentConfirmed || isEditingDisabled}
              sx={{ padding: 0 }}
            />
            <Typography variant="h5">Input Text</Typography>
            <Typography variant="h5">Measure</Typography>
            <Typography variant="h5">District</Typography>
            <div></div>
            <div></div>
          </TableHeader>
          </StickySectionHeader>
          {filteredMeasureRows.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', borderBottom: '1px solid #EEEEEE' }}>
              <Typography variant="body2" color="textSecondary">
                {consolidatedContentFilter === 'needs-review'
                  ? 'No measures need review.'
                  : 'No measures found in consolidated content.'}
              </Typography>
            </div>
          ) : (
            filteredMeasureRows.map(({ measure, index }) => {
              const districtId = measure?.matchedData?.districtId || measure?.matchedData?.district?._id || measure?.matchedData?.district?.districtId;
              const district = districtId ? raceDistricts[districtId] : null;
              const districtConfidence = typeof measure?.matchedData?.confidence === 'number'
                ? measure.matchedData.confidence
                : (typeof measure?.matchedData?.district?.confidence === 'number' ? measure.matchedData.district.confidence : 1.0);
              const missingDistrict = !districtId;
              const hasLowDistrictConfidence = !missingDistrict &&
                districtConfidence < LOW_DISTRICT_CONFIDENCE_THRESHOLD;
              const isExpanded = Boolean(measure._id && expandedMeasures.has(measure._id));

              return (
                <React.Fragment key={measure._id || `measure-${index}`}>
                  <TableRow style={{
                    gridTemplateColumns: '84px 2fr 2fr 1.5fr 100px 88px',
                    opacity: measure.removed ? 0.45 : 1,
                    padding: '12px 24px 0',
                    pointerEvents: measure.removed ? 'none' : 'auto'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                      <Checkbox
                        size="small"
                        checked={selectedMeasureIndices.has(index)}
                        inputProps={{ 'aria-label': `select measure ${measure.inputText || index + 1}` }}
                        onChange={(event) => {
                          setSelectedMeasureIndices(previous => {
                            const next = new Set(previous);
                            if (event.target.checked) {
                              next.add(index);
                            } else {
                              next.delete(index);
                            }
                            return next;
                          });
                        }}
                        disabled={measure.removed || bulkUpdatingMeasures || !isContentConfirmed || isEditingDisabled}
                        sx={{ padding: 0 }}
                      />
                      {!measure.removed && (missingDistrict || hasLowDistrictConfidence) && (
                        <Tooltip title={missingDistrict ? 'Needs district' : 'Low district confidence'} arrow>
                          <WarningIcon style={{ color: '#FF9800', fontSize: '20px' }} />
                        </Tooltip>
                      )}
                    </div>
                    <Typography variant="body2">{measure.inputText || '--'}</Typography>
                    <Typography variant="body2">{measure.measureName || '--'}</Typography>
                    <DistrictCell
                      district={district || null}
                      suggestCreationAs={measure?.matchedData?.createWithData}
                      state={election?.state || ''}
                      stateConfiguration={stateConfiguration}
                      confidence={districtConfidence}
                      onDistrictChange={(newDistrict: District) => handleMeasureDistrictChange(index, newDistrict)}
                      onDistrictClear={() => handleMeasureDistrictChange(index, null)}
                      onChangeConfidence={(confidence: number) => handleMeasureDistrictConfidenceChange(index, confidence)}
                      disableEditing={!isContentConfirmed || isEditingDisabled}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {measure.numberBallots !== undefined
                        ? `in ${measure.numberBallots ?? 0} ballot${(measure.numberBallots ?? 0) !== 1 ? 's' : ''}`
                        : '-'}
                    </Typography>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pointerEvents: 'auto'
                    }}>
                      {!measure.removed && (measure.inputTextsUnique?.length || 0) > 1 && (
                        <Tooltip title="Split grouped row">
                          <IconButton
                            aria-label="split grouped measure row"
                            size="small"
                            onClick={() => handleSplitMeasure(index)}
                            disabled={
                              !isContentConfirmed
                              || isEditingDisabled
                              || Boolean(
                                measure._id && splittingContentItemIds.has(measure._id)
                              )
                            }
                          >
                            {measure._id && splittingContentItemIds.has(measure._id)
                              ? <CircularProgress size={18} />
                              : <CallSplitIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={measure.removed ? 'Restore row' : 'Remove row'}>
                        <IconButton
                          aria-label={measure.removed ? 'restore measure row' : 'remove measure row'}
                          size="small"
                          onClick={() => handleRemoveMeasure(index, !measure.removed)}
                          disabled={!isContentConfirmed || isEditingDisabled}
                        >
                          {measure.removed
                            ? <RestoreFromTrashIcon fontSize="small" />
                            : <DeleteOutlineIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableRow>
                  <MeasureDetailsSection style={{
                    opacity: measure.removed ? 0.45 : 1,
                    pointerEvents: measure.removed ? 'none' : 'auto'
                  }}>
                    <MeasureDetailsHeader
                      type="button"
                      aria-expanded={isExpanded}
                      onClick={() => toggleMeasureExpansion(measure._id)}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      <Typography variant="body2" color="textSecondary">
                        Measure creation data
                      </Typography>
                    </MeasureDetailsHeader>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <MeasureDetailsContent>
                        <MeasureDetail>
                          <Typography variant="caption" color="textSecondary">Title</Typography>
                          {isContentConfirmed && !isEditingDisabled ? (
                            <TextField
                              key={`measure-${index}-measureName-${measure.measureName ?? ''}`}
                              defaultValue={measure.measureName || measure.inputText || ''}
                              size="small"
                              fullWidth
                              disabled={isEditingDisabled}
                              onBlur={(event) => {
                                const value = event.target.value.trim();
                                const currentValue = measure.measureName || measure.inputText || '';
                                if (value !== currentValue) {
                                  handleMeasureFieldChange(index, 'measureName', value);
                                }
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.currentTarget.blur();
                                }
                              }}
                            />
                          ) : (
                            <Typography variant="body2">{measure.measureName || measure.inputText || '--'}</Typography>
                          )}
                        </MeasureDetail>
                        <MeasureDetail>
                          <Typography variant="caption" color="textSecondary">Ballot text</Typography>
                          {isContentConfirmed && !isEditingDisabled ? (
                            <TextField
                              key={`measure-${index}-ballotText-${measure.ballotText ?? ''}`}
                              defaultValue={measure.ballotText || ''}
                              size="small"
                              fullWidth
                              multiline
                              minRows={3}
                              disabled={isEditingDisabled}
                              onBlur={(event) => {
                                const value = event.target.value.trim();
                                if (value !== (measure.ballotText || '')) {
                                  handleMeasureFieldChange(index, 'ballotText', value);
                                }
                              }}
                            />
                          ) : (
                            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                              {measure.ballotText || '--'}
                            </Typography>
                          )}
                        </MeasureDetail>
                        <MeasureDetail>
                          <Typography variant="caption" color="textSecondary">Act numbers</Typography>
                          {isContentConfirmed && !isEditingDisabled ? (
                            <TextField
                              key={`measure-${index}-actNumbers-${measure.actNumbers ?? ''}`}
                              defaultValue={measure.actNumbers || ''}
                              size="small"
                              fullWidth
                              disabled={isEditingDisabled}
                              onBlur={(event) => {
                                const value = event.target.value.trim();
                                if (value !== (measure.actNumbers || '')) {
                                  handleMeasureFieldChange(index, 'actNumbers', value);
                                }
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.currentTarget.blur();
                                }
                              }}
                            />
                          ) : (
                            <Typography variant="body2">{measure.actNumbers || '--'}</Typography>
                          )}
                        </MeasureDetail>
                      </MeasureDetailsContent>
                    </Collapse>
                  </MeasureDetailsSection>
                </React.Fragment>
              );
            })
          )}
        </div>
        {!isMatchingConfirmed && (races.length === 0 || (reviewProgress.completed === reviewProgress.total && reviewProgress.total > 0)) && (
          <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', borderTop: '1px solid #EEEEEE' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmMatching}
              size="medium"
              disabled={confirmingMatching}
              style={{ minWidth: '200px' }}
            >
              {confirmingMatching ? 'Confirming...' : isScoutingConfirmed ? 'Confirm edits' : 'Confirm Matching'}
            </Button>
          </div>
        )}
            </Content>
          </Box>
      </Box>
      )}

      {/* Scouting Details */}
      {(!enableStepNavigation || displayedStepName === 'scouting-details') && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '36px', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                Scouting Details
              </Typography>
              {autoScouter?.review?.scouting && (
                <CheckCircleIcon style={{ fontSize: '20px', color: '#4caf50' }} />
              )}
            </Box>
          </Box>
          <Box sx={{ pt: 0.5 }}>
            <Content>
              {/* Section 1: Scouting Configuration */}
              <Box sx={{ marginBottom: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, marginBottom: '16px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">
                      Determine which intersecting district types should be marked off in the scouting process.
                    </Typography>
                    {autoScouter?.review?.matching && autoScouter?.stateName && (
                      <AddDistrictTypeModal
                        stateName={autoScouter.stateName}
                        existingTypes={(autoScouter.scoutingConfiguration || []).map(c => c.districtType)}
                        excludeTypes={autoScouter.scoutingConfiguration?.map(c => c.districtType) || []}
                        restrictToTiers={[1, 2]}
                        onSelect={(districtType) => {
                          const newConfig = [...(autoScouter.scoutingConfiguration || [])];
                          // Check if type already exists (shouldn't happen due to existingTypes filter, but safety check)
                          if (!newConfig.some(c => c.districtType === districtType)) {
                            newConfig.push({
                              districtType,
                              coverageExtent: 'limited-coverage' // Default to limited-coverage
                            });
                            handleSaveScoutingConfiguration(newConfig);
                          }
                        }}
                        buttonComponent={({ onClick }) => (
                          <Tooltip title={!autoScouter?.review?.matching ? "Complete consolidated content review first" : isEditingDisabled ? "Editing disabled" : "Add type"}>
                            <span>
                              <IconButton
                                onClick={onClick}
                                size="small"
                                disabled={!autoScouter?.review?.matching || isEditingDisabled}
                                sx={{
                                  color: '#666',
                                  '&:hover': {
                                    color: '#1976d2',
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                  },
                                  '&.Mui-disabled': {
                                    color: '#ccc'
                                  }
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoOutlinedIcon style={{ fontSize: 16, cursor: 'help' }} />
                    <Typography variant="body2" color="textPrimary">
                      The defaults here are populated from the default configuration for {autoScouter?.stateName || 'this state'},{' '}
                      which you can edit&nbsp;
                      <Link
                        to={`/state-election-configurations/${(autoScouter?.stateName || '').replace(/\s+/g, '-').toLowerCase()}`}
                        style={{ textDecoration: 'underline' }}
                      >
                        here
                      </Link>
                      , or{' '}
                      <button
                        type="button"
                        onClick={handleResetScoutingConfiguration}
                        disabled={savingScoutingConfiguration || isEditingDisabled}
                        style={{
                          background: 'none',
                          border: 0,
                          padding: 0,
                          color: 'inherit',
                          cursor: savingScoutingConfiguration || isEditingDisabled ? 'default' : 'pointer',
                          font: 'inherit',
                          textDecoration: 'underline',
                          opacity: savingScoutingConfiguration || isEditingDisabled ? 0.5 : 1
                        }}
                      >
                        {savingScoutingConfiguration ? 'resetting to defaults' : 'reset to defaults'}
                      </button>
                      .
                    </Typography>
                  </Box>
                </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {(autoScouter.scoutingConfiguration || []).map((config, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body1" style={{ minWidth: '120px', fontWeight: '500' }}>
                          {humanizeDistrictType(config.districtType, stateConfiguration?.typeConfigurations || null)}
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <Select
                            value={config.coverageExtent}
                            onChange={(e) => {
                              const newConfig = [...(autoScouter.scoutingConfiguration || [])];
                              newConfig[index] = {
                                ...newConfig[index],
                                coverageExtent: e.target.value as 'full-coverage' | 'limited-coverage' | 'no-coverage'
                              };
                              handleSaveScoutingConfiguration(newConfig);
                            }}
                            disabled={!autoScouter?.review?.matching || savingScoutingConfiguration || isEditingDisabled}
                            sx={{
                              backgroundColor: 'white',
                              '& .MuiSelect-select': {
                                padding: '8px 32px 8px 12px'
                              }
                            }}
                          >
                            <MenuItem value="full-coverage">Mark all as complete</MenuItem>
                            <MenuItem value="limited-coverage">Mark only those in the content</MenuItem>
                            <MenuItem value="no-coverage">Mark none</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: '20px', paddingLeft: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autoScouter?.completeRaces !== false}
                        onChange={(e) => handleSaveCompletionTypes({ completeRaces: e.target.checked })}
                        disabled={!autoScouter?.review?.matching || savingScoutingConfiguration || isEditingDisabled}
                      />
                    }
                    label="Complete races"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={autoScouter?.completeMeasures !== false}
                        onChange={(e) => handleSaveCompletionTypes({ completeMeasures: e.target.checked })}
                        disabled={!autoScouter?.review?.matching || savingScoutingConfiguration || isEditingDisabled}
                      />
                    }
                    label="Complete measures"
                  />
                </Box>
                <Divider style={{ marginBottom: '24px' }} />

                {/* Section 2: Districts */}
                {
                  district &&
                  <Box sx={{ marginBottom: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '12px' }}>
                      <Typography variant="body1">
                        All of these districts will be marked as complete:
                      </Typography>
                      {savingScoutingConfiguration && (
                        <CircularProgress size={16} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, paddingLeft: 1 }}>
                      {[district, ...possibleDistrictsFull]
                        .filter((candidate, index, districts) => (
                          districts.findIndex(item => item._id === candidate._id) === index
                        ))
                        .map((district: District) => {
                        const isMarked = markedDistrictsSet.has(district._id);
                        if (!isMarked) return null;
                        return (
                          <Typography variant="body1" style={{ flex: 1 }} key={district._id}>
                            {district.longName || district.name || district._id}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>
                }

                {/* Confirm Scouting Button */}
                {autoScouter?.review?.matching && !autoScouter?.review?.scouting && (
                  <div style={{ padding: '24px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #EEEEEE' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleConfirmScouting}
                      size="medium"
                      disabled={confirmingScouting}
                      style={{ minWidth: '200px' }}
                    >
                      {confirmingScouting ? 'Confirming...' : 'Confirm Scouting'}
                    </Button>
                  </div>
                )}
              </Content>
            </Box>
        </Box>
      )}

      {/* Data Operations - shown after matching and scouting are confirmed */}
      {isDataOperationsReady && (!enableStepNavigation || displayedStepName === 'data-operations') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '36px', mb: 0.5, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: '48px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                    Data Operations Queue
                  </Typography>
                  {autoScouter.createStatus === 'success' && (
                    <Tooltip title="All data operations completed">
                      <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '24px' }} />
                    </Tooltip>
                  )}
                </div>
                <Typography variant="body2" color="textSecondary">
                  ({autoScouter.dataOperations?.length || 0} operation{(autoScouter.dataOperations?.length || 0) !== 1 ? 's' : ''})
                </Typography>
              </Box>
              {!hasFinishedDataOperations && (
                <Tooltip title={regeneratingDataOperations ? 'Regenerating data operations' : 'Regenerate data operations'}>
                  <IconButton
                    aria-label="Regenerate data operations"
                    disabled={regeneratingDataOperations || autoScouter?.processInProgress?.stage === 'creating'}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRegenerateDataOperations();
                    }}
                    size="small"
                  >
                    {regeneratingDataOperations ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ pt: 0.5 }}>
              {/* Creation Progress Indicator - at the top */}
              {autoScouter?.processInProgress?.stage === 'creating' && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 2,
                  padding: '16px',
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #2196f3',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  width: '100%'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2
                  }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <CircularProgress 
                        size={24} 
                        sx={{ color: '#2196f3' }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: '500' }}>
                      Creating entities from {autoScouter.dataOperations?.length || 0} operations...
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCancelCreateEntities}
                      disabled={cancellingCreateEntities}
                      sx={{ marginLeft: 'auto' }}
                    >
                      {cancellingCreateEntities ? 'Cancelling...' : 'Cancel'}
                    </Button>
                  </Box>
                  
                  {/* Progress Bar */}
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
                        Progress: {autoScouter.dataOperations?.filter(op => op.status === 'completed' || op.status === 'no-op').length || 0} of {autoScouter.dataOperations?.length || 0} operations completed
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {autoScouter.dataOperations && autoScouter.dataOperations.length > 0 ? 
                          Math.round((autoScouter.dataOperations.filter(op => op.status === 'completed' || op.status === 'no-op').length / autoScouter.dataOperations.length) * 100) : 0
                        }%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={autoScouter.dataOperations && autoScouter.dataOperations.length > 0 ? 
                        (autoScouter.dataOperations.filter(op => op.status === 'completed' || op.status === 'no-op').length / autoScouter.dataOperations.length) * 100 : 0
                      } 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}
              
              <Content>
                {autoScouter?.dataOperationsError && (
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid #f44336',
                      borderRadius: '8px',
                      backgroundColor: '#ffebee',
                      mb: 2
                    }}
                  >
                    <Typography variant="body1" sx={{ color: '#c62828', fontWeight: 600, mb: 1 }}>
                      Data operation processing failed
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b71c1c' }}>
                      {autoScouter.dataOperationsError}
                    </Typography>
                  </Box>
                )}
                <DataOperationsTable
                  dataOperations={(autoScouter.dataOperations || [])
                    .map(toActionableDataOperation)
                  }
                  title=""
                  defaultToOperationsTab={shouldDefaultToOperationsTab}
                  onChangeDataOperations={autoScouter?.createStatus === 'success' ? undefined : (operations) => {
                    const retainedOperationIds = new Set(
                      operations.map(operation => operation.uniqueId)
                    );
                    // Preserve the backend representation of no-op operations that remain in the queue.
                    const noopOperations = autoScouter.dataOperations?.filter(
                      op => (
                        op.operationType === 'noop' || (op.operationType as string) === 'no-op'
                      ) && retainedOperationIds.has(op.uniqueId)
                    ) || [];
                    const actionableOperations = operations.filter(
                      (operation): operation is DataOperation & { operationType: 'create' | 'update' | 'delete' } =>
                        operation.operationType !== 'no-op'
                    );
                    handleSaveDataOperations([...actionableOperations, ...noopOperations]);
                  }}
                  onChangeOfficialMatch={canChangeOfficialMatches ? handleChangeOfficialMatch : undefined}
                  electionKey={electionKey}
                  editingDisabled={autoScouter?.createStatus === 'success' || autoScouter?.processInProgress?.stage === 'creating'}
                  onRetryFailedOperations={(autoScouter?.createStatus === 'error' && (autoScouter?.processInProgress?.stage === 'none' || !autoScouter?.processInProgress)) ? handleCreateEntities : undefined}
                />

                {/* Create Button - only show if createStatus is not 'success' and not in error state with retry button */}
                {autoScouter?.processInProgress?.stage !== 'creating' &&
                 autoScouter?.createStatus !== 'success' &&
                 !(autoScouter?.createStatus === 'error' && (autoScouter?.processInProgress?.stage === 'none' || !autoScouter?.processInProgress)) && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px', marginBottom: '12px' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleCreateEntities}
                      style={{
                        minWidth: '200px',
                        height: '48px',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                      disabled={
                        startingCreateEntities
                        || !autoScouter.dataOperations
                        || autoScouter.dataOperations.length === 0
                      }
                    >
                      {startingCreateEntities ? 'Starting…' : 'Create'}
                    </Button>
                  </div>
                )}
              </Content>
            </Box>
        </Box>
      )}
      <Dialog
        open={deleteConfirmationOpen}
        onClose={() => {
          if (!startingCreateEntities) setDeleteConfirmationOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm delete operations</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            This will permanently delete existing election data.
          </Alert>
          <Typography variant="body1">
            The data operations include{' '}
            {[
              raceDeleteCount > 0
                ? `${raceDeleteCount} race delete operation${raceDeleteCount === 1 ? '' : 's'}`
                : null,
              measureDeleteCount > 0
                ? `${measureDeleteCount} measure delete operation${measureDeleteCount === 1 ? '' : 's'}`
                : null
            ].filter(Boolean).join(' and ')}.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmationOpen(false)}
            disabled={startingCreateEntities}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await createEntities();
              setDeleteConfirmationOpen(false);
            }}
            variant="contained"
            color="error"
            disabled={startingCreateEntities}
          >
            {startingCreateEntities ? 'Starting…' : 'Confirm and create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={addContentItemDialog !== null}
        onClose={() => {
          if (!addingContentItem) setAddContentItemDialog(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add {addContentItemDialog?.contentType || 'content'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Input text"
            margin="dense"
            inputRef={addContentInputRef}
            error={Boolean(addContentItemDialog?.error)}
            helperText={addContentItemDialog?.error || 'Enter the text as it appears on the ballot.'}
            disabled={addingContentItem}
            onChange={() => {
              if (addContentItemDialog?.error) {
                setAddContentItemDialog(current => current && ({ ...current, error: '' }));
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleAddConsolidatedContentItem();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddContentItemDialog(null)}
            disabled={addingContentItem}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddConsolidatedContentItem}
            variant="contained"
            disabled={addingContentItem}
          >
            {addingContentItem ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={errorDialogOpen}
        onClose={() => {
          setErrorDialogOpen(false);
          setErrorMessage('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {errorMessage || 'An error occurred. Please try again.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setErrorDialogOpen(false);
            setErrorMessage('');
          }} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={recrawlDialogOpen}
        onClose={() => !recrawling && setRecrawlDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Re-crawl content</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Confirm the crawl prompt and optional manual URL before re-crawling.
            </Typography>
            <TextField
              label="Manual URL (optional)"
              value={recrawlUrl}
              onChange={(e) => setRecrawlUrl(e.target.value)}
              placeholder="https://..."
              fullWidth
              size="small"
            />
            <PromptEditor
              label="Crawl prompt"
              value={recrawlPrompt}
              onChange={setRecrawlPrompt}
              placeholder="Update or override the crawl instructions. You can reference {startingUrl}, {stateName}, {districtName}, and {electionName}."
              rows={8}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecrawlDialogOpen(false)} disabled={recrawling}>
            Cancel
          </Button>
          <Button onClick={handleRecrawl} variant="contained" disabled={recrawling}>
            {recrawling ? 'Re-crawling...' : 'Re-crawl'}
          </Button>
        </DialogActions>
      </Dialog>
      <AddPotentialAutoScouterContent
        open={addContentModalOpen}
        onClose={() => setAddContentModalOpen(false)}
        onSave={handleSaveNewContent}
        existingUrls={existingUrls}
      />
      {saveIndicatorStatus !== 'idle' && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            alignItems: 'center',
            backgroundColor: saveIndicatorStatus === 'error' ? '#fff4f4' : '#fff',
            border: `1px solid ${saveIndicatorStatus === 'error' ? '#d32f2f' : '#ddd'}`,
            borderRadius: '999px',
            bottom: 20,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.14)',
            display: 'flex',
            gap: 0.75,
            padding: '6px 10px',
            position: 'fixed',
            right: 20,
            zIndex: 1400
          }}
        >
          {saveIndicatorStatus === 'saving' && <CircularProgress size={14} />}
          {saveIndicatorStatus === 'saved' && (
            <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
          )}
          {saveIndicatorStatus === 'error' && (
            <WarningIcon sx={{ color: '#d32f2f', fontSize: 16 }} />
          )}
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {saveIndicatorStatus === 'saving'
              ? (pendingSaveCount > 1 ? `Saving ${pendingSaveCount} changes…` : 'Saving…')
              : (saveIndicatorStatus === 'saved' ? 'Saved' : 'Save failed')}
          </Typography>
        </Box>
      )}
    </Wrapper>
  );
};

const AutoScouterDetail = () => {
  const { key: electionKey, id: autoScouterId, stepName } = useParams<{ key: string; id: string; stepName?: string }>();

  if (!electionKey || !autoScouterId) {
    return (
      <Wrapper>
        <Typography variant="body1" color="textSecondary">
          Missing election or auto-scouter id.
        </Typography>
      </Wrapper>
    );
  }

  return (
    <AutoScouterDetailContent
      electionKey={electionKey}
      autoScouterId={autoScouterId}
      showToolbar
      enableStepNavigation
      activeStepName={stepName}
    />
  );
};

// Styled components
const Wrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const SummarySection = styled.div`
  background: #F7F9FE;
  border-radius: 8px;
  border: 1px solid #EEEEEE;
  padding: 12px 24px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DistrictRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DistrictInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ElectionHomepageRow = styled.div`
  padding-top: 12px;
`;

const ElectionHomepageContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const ElectionHomepageActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const Content = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const StickySectionHeader = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  position: sticky;
  top: 72px;
  z-index: 10;
  background: #F7F9FE;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
`;

const SectionToolbar = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 48px;
  padding: 4px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ProgressSection = styled.div`
  width: 100%;
  min-width: 0;
  position: sticky;
  top: 0;
  z-index: 11;
  padding: 12px 24px;
  height: 72px;
  box-sizing: border-box;
  background: #F7F9FE;
  border-bottom: 1px solid #EEEEEE;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ProgressInfo = styled.div`
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableHeader = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: 40px 2fr 1fr 1fr 80px 1fr 60px 56px;
  grid-gap: 24px;
  padding: 8px 24px 12px;
  background: #F7F9FE;
  border-bottom: 1px solid #EEEEEE;
  align-items: center;

  > * {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  h5 {
    margin: 0;
    font-weight: bold;
    color: #313639;
  }
`;

const TableRow = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: 40px 2fr 1fr 1fr 80px 1fr 60px 56px;
  grid-gap: 24px;
  padding: 16px 24px 0px;
  align-items: center;

  > * {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const CandidatesSection = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 8px 24px 12px 94px;
  border-bottom: 1px solid #EEEEEE;
`;

const CandidatesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.7;
  }
`;

const CandidatesContent = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 1000px;
`;

const MeasureDetailsSection = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 8px 24px 12px 88px;
  border-bottom: 1px solid #EEEEEE;
`;

const MeasureDetailsHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;

  &:hover {
    opacity: 0.7;
  }
`;

const MeasureDetailsContent = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 800px;
  padding: 12px 16px;
  border-radius: 4px;
  background: #F7F9FE;
`;

const MeasureDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CandidateRow = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 2.5fr) minmax(0, 0.45fr) minmax(0, 1fr) 40px;
  column-gap: 24px;
  padding: 8px 0;
  align-items: center;
  border-bottom: 1px solid #EEEEEE;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CandidateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CandidateBallotCount = styled.div`
  display: flex;
  align-items: center;
`;

const CandidateParty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const CandidateActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const AddCandidateRow = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  margin-top: 4px;
  max-width: 1000px;
`;

const LoadingTile = styled.div`
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;

  span {
    margin-bottom: 20px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #EEEEEE;
`;

const ScrapedContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const Divider = styled.hr`
  width: 100%;
  background-color: #e3e3e3;
  margin: 0;
  border: none;
  height: 1px;
`;
export default AutoScouterDetail;
export { AutoScouterDetailContent };

